<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Document Control</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- Breadcrumb -->
      <v-breadcrumbs :items="breadcrumbItems" class="px-0 mt-2">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <v-card class="card2" style="margin-top: 0.5rem">
        <!-- +Add button row -->
        <v-row align="center" class="mx-4 mt-2 mb-1" no-gutters>
          <v-col class="d-flex flex-wrap align-center gap-2">
            <v-btn v-if="canAdd" variant="elevated" class="custom-document-btn" @click="addDocument">
              <v-icon size="23" class="mr-2">mdi-plus</v-icon>
              <span class="btn-text">Add</span>
            </v-btn>
          </v-col>
          <v-col class="d-flex flex-wrap align-center justify-end gap-2">
            <v-btn variant="elevated" class="custom-document-btn" :loading="loadingTable" @click="loadDocumentVersions">
              <v-icon size="23" class="mr-2">mdi-refresh</v-icon>
              <span class="btn-text">Refresh</span>
            </v-btn>
          </v-col>
        </v-row>
        <!-- Document heading -->
        <div class="mx-4 mt-2 mb-1">
          <span class="text-h6 font-weight-bold">Document</span>
        </div>

        <!-- Data Table -->
        <div class="ma-4 doc-table-container">
          <v-data-table-server
            v-model:options="options"
            :items-per-page="-1"
            :items-per-page-options="[{ value: -1, title: 'All' }]"
            :items-length="totalItems"
            :items="tableData"
            :headers="headers"
            :loading="loadingTable"
            class="doc-table"
            density="comfortable"
            fixed-header
          >
            <template v-slot:bottom></template>
            <template v-slot:item="{ item }">
              <tr>
                <!-- Actions -->
                <td class="sticky-action" style="text-align: center">
                  <div class="action-buttons-group">
                    <v-btn
                      size="small"
                      :variant="isViewDisabled(item) ? 'tonal' : 'outlined'"
                      :color="isViewDisabled(item) ? 'grey' : 'primary'"
                      class="action-btn"
                      :disabled="isViewDisabled(item)"
                      @click="viewDocument(item)"
                    >
                      <v-icon size="16">mdi-eye</v-icon>
                      <v-tooltip activator="parent" location="top">
                        {{ isViewDisabled(item) ? 'Wait for processing to finish' : 'View' }}
                      </v-tooltip>
                    </v-btn>
                    <v-btn v-if="canEdit" size="small" variant="outlined" color="primary" class="action-btn" @click="editDocument(item)">
                      <v-icon size="16">mdi-pencil</v-icon>
                      <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                    </v-btn>
                    <v-btn size="small" variant="outlined" color="success" class="action-btn" @click="exportDocument(item)">
                      <v-icon size="16">mdi-download</v-icon>
                      <v-tooltip activator="parent" location="top">Export</v-tooltip>
                    </v-btn>
                  </div>
                </td>
                <!-- Version -->
                <td style="text-align: center">{{ item.version || '-' }}</td>
                <!-- Status -->
                <td style="text-align: center">
                  <span :style="{ color: item.status === 'Active' ? '#4CAF50' : '#9E9E9E', fontWeight: 500 }">
                    {{ item.status || '-' }}
                  </span>
                </td>
                <!-- Status Progress -->
                <td style="text-align: center">
                  <v-chip
                    v-if="item.statusProcess"
                    :color="processingChipColor(item.statusProcess)"
                    variant="flat"
                    size="small"
                  >
                    <v-icon size="14" class="mr-1">{{ processingChipIcon(item.statusProcess) }}</v-icon>
                    <span>{{ item.statusProcessLabel || item.statusProcess }}</span>
                  </v-chip>
                  <span v-else>-</span>
                </td>
                <!-- File_name -->
                <td style="text-align: left; padding-left: 16px">{{ item.fileName || '-' }}</td>
                <!-- Effective_start_date -->
                <td style="text-align: left; padding-left: 16px">{{ item.effectiveStartDate || '-' }}</td>
                <!-- Effective_end_date -->
                <td style="text-align: left; padding-left: 16px">{{ item.effectiveEndDate || '-' }}</td>
                <!-- Universal link -->
                <td style="text-align: left; padding-left: 16px">{{ item.universalLink || '-' }}</td>
                <!-- Web_link -->
                <td style="text-align: left; padding-left: 16px">{{ item.webLink || '-' }}</td>
                <!-- JSON -->
                <td style="text-align: left; padding-left: 16px">{{ item.json || '-' }}</td>
                <!-- Channel_availability -->
                <td style="text-align: left; padding-left: 16px">{{ item.channelAvailability || '-' }}</td>
                <!-- Added_by -->
                <td style="text-align: left; padding-left: 16px">{{ item.addedBy || '-' }}</td>
                <!-- Added_date -->
                <td style="text-align: left; padding-left: 16px">{{ item.addedDate || '-' }}</td>
                <!-- Last_Update_By -->
                <td style="text-align: left; padding-left: 16px">{{ item.updateBy || '-' }}</td>
                <!-- Last_Update_Date -->
                <td style="text-align: left; padding-left: 16px">{{ item.updatedDate || '-' }}</td>
                <!-- Note -->
                <td style="text-align: left; padding-left: 16px">{{ item.note || '-' }}</td>
              </tr>
            </template>
          </v-data-table-server>
        </div>
      </v-card>

      <!-- Interest Rate Control -->
      <v-card v-if="showInterestRateControl" class="card2" style="margin-top: 0.5rem">
        <v-row align="center" class="mx-4 mt-2 mb-1" no-gutters>
          <v-col class="d-flex flex-wrap align-center gap-2">
            <v-btn v-if="canAdd" variant="elevated" class="custom-document-btn" @click="addInterestRate()">
              <v-icon size="23" class="mr-2">mdi-plus</v-icon>
              <span class="btn-text">Add</span>
            </v-btn>
          </v-col>
          <v-col class="d-flex flex-wrap align-center justify-end gap-2">
            <v-btn variant="elevated" class="custom-document-btn" @click="openCategoriesDialog">
              <v-icon size="23" class="mr-2">mdi-shape</v-icon>
              <span class="btn-text">Categories</span>
            </v-btn>
          </v-col>
        </v-row>


        <!-- Interest Rate Control heading -->
        <div class="mx-4 mt-2 mb-1">
          <span class="text-h6 font-weight-bold">Interest Rate Control</span>
        </div>

        <div v-if="!interestRateGroups.length" class="ma-4 text-center text-grey py-6" style="border: 1px solid #e0e0e0; border-radius: 8px;">
          No interest rates yet.
        </div>

        <!-- One table per accountCode (the metadoc is the parent group). Account Name is a per-row column (versions may differ). -->
        <div v-for="group in interestRateGroups" :key="group.accountCode || 'default'" class="ma-4 interest-table-container">
          <div v-if="group.accountCode" class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-1 font-weight-bold">{{ group.accountCode }}</span>
            <v-btn v-if="canAdd && productCategory === 'Deposit'" size="small" variant="outlined" color="primary"
                   @click="addInterestRate({ accountCode: group.accountCode })">
              <v-icon size="16" class="mr-1">mdi-plus</v-icon>
              Add Version
            </v-btn>
          </div>
          <v-data-table
            :items="group.rows"
            :headers="interestRateHeaders"
            :items-per-page="-1"
            class="interest-table"
            density="comfortable"
            fixed-header
          >
            <template v-slot:bottom></template>
            <template v-slot:item="{ item }">
              <tr>
                <!-- Actions -->
                <td style="text-align: center">
                  <div class="action-buttons-group">
                    <v-btn size="small" variant="outlined" color="primary" @click="viewInterestRate(item)" class="action-btn">
                      <v-icon size="16">mdi-eye</v-icon>
                      <v-tooltip activator="parent" location="top">View</v-tooltip>
                    </v-btn>
                    <v-btn v-if="canEdit" size="small" variant="outlined" color="primary" class="action-btn" @click="editInterestRate(item)">
                      <v-icon size="16">mdi-pencil</v-icon>
                      <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                    </v-btn>
                  </div>
                </td>
                <!-- Version -->
                <td style="text-align: center">{{ item.version || '-' }}</td>
                <!-- Status -->
                <td style="text-align: center">
                  <span :style="{ color: item.status === 'Active' ? '#4CAF50' : '#9E9E9E', fontWeight: 500 }">
                    {{ item.status || '-' }}
                  </span>
                </td>
                <!-- Account Name -->
                <td style="text-align: left; padding-left: 16px">{{ item.accountName || '-' }}</td>
                <!-- Customer Scope -->
                <td v-if="productCategory === 'Deposit'" style="text-align: center">
                  <v-chip color="#4B2869" size="small" variant="outlined" class="customer-scope-chip">
                    {{ getCustomerScopeText(item.isNewUser) }}
                  </v-chip>
                </td>
                <!-- Effective_start_date -->
                <td style="text-align: left; padding-left: 16px">{{ item.effectiveStartDate || '-' }}</td>
                <!-- Effective_end_date -->
                <td style="text-align: left; padding-left: 16px">{{ item.effectiveEndDate || '-' }}</td>
                <!-- Added_by -->
                <td style="text-align: left; padding-left: 16px">{{ item.addedBy || '-' }}</td>
                <!-- Added_date -->
                <td style="text-align: left; padding-left: 16px">{{ item.addedDate || '-' }}</td>
                <!-- Last_Update_By -->
                <td style="text-align: left; padding-left: 16px">{{ item.updateBy || '-' }}</td>
                <!-- Last_Update_Date -->
                <td style="text-align: left; padding-left: 16px">{{ item.updatedDate || '-' }}</td>
                <!-- Note -->
                <td style="text-align: left; padding-left: 16px">{{ item.note || '-' }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card>

      <!-- Back Button -->
      <div class="mt-4 mb-4">
        <v-btn variant="outlined" color="primary" @click="goBack">
          <v-icon start>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </div>
    </v-container>

    <!-- Add New Document Dialog -->
    <v-dialog v-model="dialogAddDoc" persistent max-width="780px">
      <v-card class="add-doc-dialog-card">
        <!-- Pink gradient header -->
        <div class="add-doc-dialog-header">
          <h2>Add New Document</h2>
        </div>

        <v-card-text class="pa-6">
          <v-form ref="addDocForm">
          <!-- Add Document + Upload -->
          <div class="mb-4">
            <label class="field-label">Add Document</label>
            <div class="d-flex align-center gap-2">
              <v-text-field
                v-model="newDoc.fileName"
                :rules="[documentRules.fileRequired]"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                readonly
                :placeholder="newDoc.file ? '' : 'No file selected'"
              ></v-text-field>
              <v-btn color="success" variant="elevated" class="upload-btn" @click="$refs.fileInput.click()">
                <v-icon start size="18">mdi-upload</v-icon>
                Upload
              </v-btn>
              <input ref="fileInput" type="file" hidden @change="onFileSelected($event)" />
            </div>
          </div>

          <!-- Effective Start / End -->
          <v-row class="mb-4">
            <v-col cols="6">
              <label class="field-label">Effective_Start (Time Stamp)</label>
              <VueDatePicker
                :model-value="newDoc.effectiveStart"
                @update:model-value="(v) => onDocDateChange(newDoc, 'effectiveStart', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
            <v-col cols="6">
              <label class="field-label">Effective_End (Time Stamp)</label>
              <VueDatePicker
                :model-value="newDoc.effectiveEnd"
                @update:model-value="(v) => onDocDateChange(newDoc, 'effectiveEnd', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
          </v-row>

          <!-- JSON / Web Link / Universal Link -->
          <v-row class="mb-4">
            <v-col cols="4">
              <label class="field-label">JSON</label>
              <v-textarea
                v-model="newDoc.json"
                variant="outlined"
                density="comfortable"
                hide-details
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col cols="4">
              <label class="field-label">Web Link</label>
              <v-textarea
                v-model="newDoc.webLink"
                variant="outlined"
                density="comfortable"
                hide-details
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col cols="4">
              <label class="field-label">Universal Link</label>
              <v-textarea
                v-model="newDoc.universalLink"
                variant="outlined"
                density="comfortable"
                hide-details
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- VectorType + Processing Method -->
          <div class="mb-1" v-if="!newDoc.file">
            <span class="text-caption text-medium-emphasis">
              <span class="text-error">*</span> Please select the document you want to upload first
            </span>
          </div>
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">VectorType *</label>
              <v-select
                :items="filterdatavectortype"
                item-title="name"
                item-value="value"
                v-model="newDoc.vectorType"
                :rules="[v => !newDoc.file || (v !== null && v !== undefined) || 'VectorType is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                :disabled="!newDoc.file"
                @update:modelValue="onVectorTypeChange"
              >
                <template v-slot:append-inner>
                  <v-tooltip location="top" max-width="300px">
                    <template v-slot:activator="{ props }">
                      <v-icon size="small" v-bind="props" class="help-icon">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Pick the type of content. This determines which processing methods are available.</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="6">
              <label class="field-label">Processing Method *</label>
              <v-autocomplete
                :items="filterDDType"
                item-title="name"
                item-value="value"
                v-model="newDoc.modelType"
                :rules="[v => !newDoc.file || (v !== null && v !== undefined) || 'Processing Method is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                :disabled="!newDoc.file || newDoc.vectorType === null || newDoc.vectorType === undefined"
                @update:modelValue="onModelTypeChange"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.raw.name">
                    <template v-slot:append>
                      <v-tooltip location="right" max-width="400px">
                        <template v-slot:activator="{ props: tipProps }">
                          <v-icon v-bind="tipProps" size="small">mdi-information-outline</v-icon>
                        </template>
                        <div class="tooltip-image"><img :src="item.raw.image" width="300" /></div>
                      </v-tooltip>
                    </template>
                  </v-list-item>
                </template>
                <template v-slot:append-inner>
                  <v-tooltip location="top" max-width="300px">
                    <template v-slot:activator="{ props }">
                      <v-icon size="small" v-bind="props" class="help-icon">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Available methods are filtered by the chosen VectorType.</span>
                  </v-tooltip>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Method-specific helper fields -->
          <v-row class="mb-4" v-if="newDoc.modelType === 3 || newDoc.modelType === 2 || newDoc.modelType === 9 || newDoc.modelType === 10 || newDoc.modelType === 11">
            <v-col cols="12" md="4" v-show="newDoc.modelType === 3 || newDoc.modelType === 10">
              <label class="field-label">Custom Character</label>
              <v-text-field
                v-model="newDoc.customCharacter"
                :rules="[v => (newDoc.modelType !== 3 && newDoc.modelType !== 10) || (!!v && String(v).trim().length > 0) || 'Custom Character is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="\n"
                hint="พิมพ์ \n = ตัด chunk ตรงบรรทัดว่าง (ทีละหัวข้อ / ย่อหน้า)"
                persistent-hint
              />
            </v-col>

            <v-col cols="12" md="4" v-show="newDoc.modelType === 2">
              <label class="field-label">Max Limited Character</label>
              <v-text-field
                v-model.number="newDoc.maxLimitedCharacter"
                :rules="[v => newDoc.modelType !== 2 || (Number.isFinite(v) && v > 0) || 'Max Limited Character is required']"
                type="number"
                min="1"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="e.g. 5000"
              />
            </v-col>

            <v-col cols="12" md="4" v-show="newDoc.modelType === 9 || newDoc.modelType === 11">
              <label class="field-label">Chunk Overlap (Lap)</label>
              <v-text-field
                v-model.number="newDoc.chunkOverlapChars"
                type="number"
                min="0"
                max="1000"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="0"
              />
            </v-col>
          </v-row>

          <!-- LLM Content Refinement (#720): จัด paragraph + เลขไทย → อารบิก ก่อนตัด chunk -->
          <div
            class="refine-toggle-bar mb-4"
            :class="{ 'refine-toggle-bar--on': newDoc.refineWithLLM, 'refine-toggle-bar--disabled': !supportsRefine }"
          >
            <v-icon class="refine-toggle-bar__icon" :color="newDoc.refineWithLLM ? '#76237E' : 'grey'">mdi-auto-fix</v-icon>
            <div class="refine-toggle-bar__text">
              <div class="refine-toggle-bar__title">Enable LLM Content Refinement</div>
              <div class="refine-toggle-bar__hint">{{ refineHint }}</div>
            </div>
            <v-switch
              v-model="newDoc.refineWithLLM"
              :disabled="!supportsRefine"
              color="#76237E"
              density="compact"
              hide-details
              inset
              class="refine-toggle-bar__switch"
            />
          </div>

          <!-- Channel availability -->
          <div class="mb-4">
            <label class="field-label">Channel availability</label>
            <div class="d-flex flex-column">
              <v-checkbox v-model="newDoc.channels" value="Mobile" label="Mobile" density="compact" hide-details class="custom-checkbox"></v-checkbox>
              <v-checkbox v-model="newDoc.channels" value="CRM" label="CRM" density="compact" hide-details class="custom-checkbox"></v-checkbox>
              <div class="d-flex align-center">
                <v-checkbox v-model="newDoc.channelOtherEnabled" label="Other :" density="compact" hide-details class="custom-checkbox"></v-checkbox>
                <v-text-field
                  v-model="newDoc.channelOther"
                  variant="underlined"
                  density="compact"
                  hide-details
                  :disabled="!newDoc.channelOtherEnabled"
                  class="ml-2"
                  style="max-width: 200px"
                ></v-text-field>
              </div>
            </div>
          </div>

          <!-- Note -->
          <div class="mb-4">
            <label class="field-label">Note</label>
            <v-textarea
              v-model="newDoc.note"
              variant="outlined"
              density="comfortable"
              hide-details
              rows="3"
            ></v-textarea>
          </div>

          <!-- Status -->
          <div class="mb-2">
            <label class="field-label">Status</label>
            <v-radio-group v-model="newDoc.status" inline hide-details>
              <v-radio label="Active" value="Active"></v-radio>
              <v-radio label="Inactive" value="Inactive"></v-radio>
            </v-radio-group>
          </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" @click="dialogAddDoc = false">Cancel</v-btn>
          <v-btn v-if="canAdd" variant="elevated" color="#1976D2" @click="saveNewDocument">
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Document Dialog -->
    <v-dialog v-model="dialogEditDoc" persistent max-width="680px">
      <v-card class="add-doc-dialog-card">
        <!-- Pink gradient header -->
        <div class="add-doc-dialog-header">
          <h2>Edit Document : {{ editDoc.itemName }} + {{ editDoc.version }}</h2>
        </div>

        <v-card-text class="pa-6">
          <v-form ref="editDocForm">
          <!-- Effective Start / End -->
          <v-row class="mb-4">
            <v-col cols="6">
              <label class="field-label">Effective_Start (Time Stamp)</label>
              <VueDatePicker
                :model-value="editDoc.effectiveStart"
                @update:model-value="(v) => onDocDateChange(editDoc, 'effectiveStart', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
            <v-col cols="6">
              <label class="field-label">Effective_End (Time Stamp)</label>
              <VueDatePicker
                :model-value="editDoc.effectiveEnd"
                @update:model-value="(v) => onDocDateChange(editDoc, 'effectiveEnd', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
          </v-row>

          <!-- Note -->
          <div class="mb-4">
            <label class="field-label">Note</label>
            <v-textarea
              v-model="editDoc.note"
              variant="outlined"
              density="comfortable"
              hide-details
              rows="3"
            ></v-textarea>
          </div>

          <!-- Status -->
          <div class="mb-2">
            <label class="field-label">Status</label>
            <v-radio-group v-model="editDoc.status" hide-details>
              <v-radio label="Active" value="Active"></v-radio>
              <v-radio label="Inactive" value="Inactive"></v-radio>
            </v-radio-group>
          </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" @click="dialogEditDoc = false">Cancel</v-btn>
          <v-btn v-if="canEdit" variant="elevated" color="#1976D2" @click="saveEditDocument">
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Document Dialog -->
    <v-dialog v-model="dialogViewDoc" max-width="820px">
      <v-card class="view-doc-dialog-card">
        <!-- Pink gradient header -->
        <div class="add-doc-dialog-header">
          <h2>View Document : {{ viewDoc.itemName }} + {{ viewDoc.version }}</h2>
        </div>

        <v-card-text class="pa-5">
          <!-- Chunk Table -->
          <label class="field-label mb-2">Chunk Table ({{ viewDoc.chunks.length }})</label>
          <div class="chunk-table-wrapper">
            <table class="chunk-table">
              <thead><tr><th style="width: 40px">No.</th><th>Content</th></tr></thead>
              <tbody>
                <tr v-for="(chunk, i) in viewDoc.chunks" :key="'c'+i">
                  <td class="text-center">{{ i + 1 }}</td>
                  <td class="chunk-cell">
                    <div class="d-flex align-start">
                      <span class="flex-grow-1">{{ chunk.text }}</span>
                      <v-btn v-if="canEdit" icon variant="text" size="x-small" @click="openEditChunk(chunk)" class="ml-1 copy-icon">
                        <v-icon size="14">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon variant="text" size="x-small" @click="copyToClipboard(chunk.text)" class="ml-1 copy-icon">
                        <v-icon size="14">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <tr v-if="!viewDoc.chunks.length"><td colspan="2" class="text-center text-grey">No data</td></tr>
              </tbody>
            </table>
          </div>

          <!-- JSON / Web Link / Universal Link -->
          <v-row class="mt-5 mb-4">
            <v-col cols="4">
              <label class="field-label">JSON</label>
              <v-text-field v-model="viewDoc.json" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="4">
              <label class="field-label">Web Link</label>
              <v-text-field v-model="viewDoc.webLink" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="4">
              <label class="field-label">Universal Link</label>
              <v-text-field v-model="viewDoc.universalLink" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
          </v-row>

          <!-- Channel availability -->
          <div class="mb-4">
            <label class="field-label">Channel_availability</label>
            <div class="d-flex flex-column">
              <v-checkbox v-model="viewDoc.channels" value="Mobile" label="Mobile" density="compact" hide-details disabled class="custom-checkbox"></v-checkbox>
              <v-checkbox v-model="viewDoc.channels" value="CRM" label="CRM" density="compact" hide-details disabled class="custom-checkbox"></v-checkbox>
              <div class="d-flex align-center">
                <v-checkbox v-model="viewDoc.channelOtherEnabled" label="Other :" density="compact" hide-details disabled class="custom-checkbox"></v-checkbox>
                <v-text-field v-model="viewDoc.channelOther" variant="underlined" density="compact" hide-details disabled class="ml-2" style="max-width: 200px"></v-text-field>
              </div>
            </div>
          </div>

          <!-- Note -->
          <div class="mb-4">
            <label class="field-label">Note</label>
            <v-textarea v-model="viewDoc.note" variant="outlined" density="comfortable" hide-details readonly rows="3"></v-textarea>
          </div>

          <!-- Status -->
          <div class="mb-2">
            <label class="field-label">Status</label>
            <v-radio-group v-model="viewDoc.status" hide-details disabled>
              <v-radio label="Active" value="Active"></v-radio>
              <v-radio label="Inactive" value="Inactive"></v-radio>
            </v-radio-group>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="dialogViewDoc = false">CLOSE</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Chunk Dialog -->
    <v-dialog v-model="dialogEditChunk" persistent max-width="760px">
      <v-card class="view-doc-dialog-card">
        <div class="add-doc-dialog-header">
          <h2>Edit Chunk</h2>
        </div>

        <v-card-text class="pa-5">
          <label class="field-label">Content</label>
          <v-textarea
            v-model="editChunk.text"
            variant="outlined"
            auto-grow
            rows="12"
            counter
            hide-details="auto"
            placeholder="Enter chunk content"
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" :disabled="savingChunk" @click="closeEditChunk">Cancel</v-btn>
          <v-btn v-if="canEdit" variant="elevated" color="#1976D2" :loading="savingChunk" @click="saveEditChunk">
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add New Interest Dialog -->
    <v-dialog v-model="dialogAddInterestRate" persistent max-width="960px">
      <v-card class="add-doc-dialog-card">
        <div class="add-doc-dialog-header">
          <h2>Add New Interest</h2>
        </div>
        <v-card-text class="pa-6">
          <v-form ref="addInterestForm">
          <!-- Account Code / Name (Deposit) -->
          <v-row v-if="newInterestRate.productCategory === 'Deposit'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Account_code <span style="color: red;">*</span></label>
              <v-text-field
                v-model="newInterestRate.accountCode"
                :rules="[
                  v => !!v || 'Account code is required',
                  v => lockAccountCode || !existingAccountCodes.includes((v || '').trim().toLowerCase()) || 'Account code already exists (use Add Version)'
                ]"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="e.g. SAVE-A"
                :readonly="lockAccountCode"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Account_name <span style="color: red;">*</span></label>
              <v-text-field
                v-model="newInterestRate.accountName"
                :rules="[v => !!v || 'Account name is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="Display name"
              ></v-text-field>
            </v-col>
          </v-row>
          <!-- Row 1: Product Category / Type / Subtype -->
          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <label class="field-label">Product_category <span style="color: red;">*</span></label>
              <v-select
                v-model="newInterestRate.productCategory"
                :items="productCategoryOptions"
                :rules="[v => !!v || 'Product category is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="Select Category"
                disabled
                @update:model-value="onProductCategoryChange(newInterestRate)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <label class="field-label">Product_type <span style="color: red;">*</span></label>
              <v-select
                v-model="newInterestRate.productType"
                :items="filteredProductTypes"
                :rules="[v => !!v || 'Product type is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="Select Type"
                :disabled="!newInterestRate.productCategory || !!subTitle"
                @update:model-value="onProductTypeChange(newInterestRate)"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <label class="field-label">Product_subtype <span style="color: red;">*</span></label>
              <v-select
                v-model="newInterestRate.productSubtype"
                :items="filteredProductSubtypes"
                :rules="[v => !!v || 'Product subtype is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="Select Subtype"
                :disabled="!newInterestRate.productType || !!productSubtype"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Meta Category / SubCategory (Deposit) -->
          <v-row v-if="newInterestRate.productCategory === 'Deposit'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Category</label>
              <v-select
                v-model="newInterestRate.categoryID"
                :items="metaCategoryOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                placeholder="Select Category"
                @update:model-value="onNewInterestCategoryChange"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Subcategory</label>
              <v-select
                v-model="newInterestRate.subCategoryID"
                :items="metaSubCategoryOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                placeholder="Select Subcategory"
                :disabled="!newInterestRate.categoryID"
              ></v-select>
            </v-col>
          </v-row>

          <div v-if="newInterestRate.productCategory === 'Deposit'" class="mb-4">
            <div class="customer-scope-toggle">
              <div>
                <div class="customer-scope-title">{{ getCustomerScopeToggleTitle(newInterestRate.isNewUser) }}</div>
                <div class="customer-scope-description">{{ getCustomerScopeDescription(newInterestRate.isNewUser) }}</div>
              </div>
              <v-switch v-model="newInterestRate.isNewUser" color="primary" inset hide-details />
            </div>
          </div>

          <!-- Row 2: Calculation Method / Interest Payment Frequency (Deposit only) -->
          <v-row v-if="newInterestRate.productCategory !== 'Loan'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Calculation Method <span style="color: red;">*</span></label>
              <v-select
                v-model="newInterestRate.calculationMethod"
                :items="calculationMethodOptions"
                item-title="title"
                item-value="value"
                :rules="[v => !!v || 'Calculation method is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="Select Method"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Interest Payment Frequency <span style="color: red;">*</span></label>
              <v-select
                v-model="newInterestRate.paymentFrequency"
                :items="paymentFrequencyOptions"
                :rules="[v => !!v || 'Payment frequency is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                placeholder="Select Frequency"
              ></v-select>
            </v-col>
          </v-row>

          <!-- ═══ Interest Rate Table: DEPOSIT (tier-based) ═══ -->
          <div v-if="newInterestRate.productCategory === 'Deposit'" class="mb-4">
            <label class="field-label">Interest Rate Table</label>
            <div class="mb-2">
              <v-btn variant="outlined" size="small" color="#4B2869" @click="addTierToList(newInterestRate.tiers)">
                <v-icon start size="16">mdi-plus</v-icon>
                ADD TIER
              </v-btn>
            </div>
            <div class="tier-table-wrapper">
              <table class="tier-table">
                <thead>
                  <tr>
                    <th style="width: 70px;">Action</th>
                    <th>Tier Name</th>
                    <th>Lower Amount</th>
                    <th>Upper Amount</th>
                    <th>Interest Rate</th>
                    <th>Min Day</th>
                    <th>Max Day</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!newInterestRate.tiers || newInterestRate.tiers.length === 0">
                    <td colspan="7" class="text-center text-grey py-3" style="font-size: 13px;">
                      No tiers added. Click "+ ADD TIER" to begin.
                    </td>
                  </tr>
                  <tr v-for="(tier, index) in (newInterestRate.tiers || [])" :key="index">
                    <td class="text-center">
                      <v-btn variant="outlined" size="x-small" color="error" @click="removeTierFromList(newInterestRate.tiers, index)">
                        Delete
                      </v-btn>
                    </td>
                    <td>
                      <v-text-field v-model="tier.tierName" variant="outlined" density="compact" hide-details placeholder="Tier name"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="tier.lowerAmount" variant="outlined" density="compact" hide-details placeholder="0.00" type="number"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="tier.upperAmount" variant="outlined" density="compact" hide-details placeholder="0.00" type="number"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="tier.interestRate" variant="outlined" density="compact" hide-details placeholder="0.00%" type="number" step="0.01"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="tier.minDay" variant="outlined" density="compact" hide-details placeholder="0" type="number"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="tier.maxDay" variant="outlined" density="compact" hide-details placeholder="0" type="number"></v-text-field>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ═══ HOME LOAN: MRR Rate + Variant Expansion Panels ═══ -->
          <div v-if="newInterestRate.productType === 'Home_Loan'" class="mb-4">
            <!-- MRR Rate -->
            <div class="mb-4">
              <div class="text-subtitle-2 mb-2" style="color: #333; font-weight: 500;">MRR Rate</div>
              <div class="d-flex align-center" style="gap: 16px;">
                <v-text-field
                  v-model.number="newInterestRate.mrrRate"
                  placeholder="MRR Rate %"
                  variant="outlined"
                  density="comfortable"
                  type="number"
                  step="0.01"
                  hide-details="auto"
                  style="flex: 0 1 400px;"
                  @input="calculateAllTotalRatesForAllVariants"
                />
                <div style="background-color: #e3f2fd; padding: 12px 20px; border-radius: 8px; border: 1px solid #90caf9; white-space: nowrap;">
                  <div style="color: #1976d2; font-size: 14px; font-weight: 500;">MRR Rate = {{ formatPercentDisplay(newInterestRate.mrrRate) }}%</div>
                  <div style="color: #1976d2; font-size: 11px;">(Current Input)</div>
                </div>
              </div>
            </div>

            <!-- Variants Section -->
            <div class="mb-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <label class="field-label">Interest Rate Variants</label>
                <v-btn size="small" color="primary" variant="outlined" @click="addVariant">
                  <v-icon start size="small">mdi-plus</v-icon>
                  Add Variant
                </v-btn>
              </div>

              <v-expansion-panels variant="accordion" class="variant-panels">
                <v-expansion-panel
                  v-for="(variant, variantIndex) in newInterestRate.variants"
                  :key="variantIndex"
                  elevation="0"
                  class="variant-panel"
                >
                  <v-expansion-panel-title class="variant-panel-title">
                    <div class="variant-header">
                      <div class="variant-header-left">
                        <div class="variant-number">
                          <span class="variant-number-text">{{ variantIndex + 1 }}</span>
                        </div>
                        <div class="variant-input-wrapper">
                          <v-icon class="variant-input-icon" size="18">mdi-tag-outline</v-icon>
                          <v-text-field
                            v-model="variant.variantName"
                            placeholder="Variant name"
                            variant="plain"
                            density="compact"
                            hide-details
                            class="variant-name-input"
                            @click.stop
                          />
                        </div>
                        <div class="variant-input-wrapper ml-4" style="min-width: 320px;">
                          <v-icon class="variant-input-icon" size="18">mdi-calculator-variant</v-icon>
                          <span class="default-calc-label">Installment:</span>
                          <v-select
                            v-model="variant.installmentType"
                            :items="installmentTypes"
                            item-title="name"
                            item-value="value"
                            variant="plain"
                            density="compact"
                            hide-details
                            class="installment-type-select"
                            @click.stop
                          />
                        </div>
                      </div>
                      <div class="variant-header-right">
                        <v-btn
                          v-if="newInterestRate.variants.length > 1"
                          class="variant-delete-btn"
                          size="small"
                          variant="tonal"
                          color="error"
                          @click.stop="removeVariant(variantIndex)"
                        >
                          <v-icon size="18">mdi-trash-can-outline</v-icon>
                          <span class="ml-1">Delete</span>
                        </v-btn>
                      </div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-card elevation="0" style="border-radius: 12px; overflow: hidden; margin-top: 12px;">
                      <v-table density="comfortable" class="interest-rate-table">
                        <thead>
                          <tr style="background-color: #fafafa;">
                            <th rowspan="2" class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; vertical-align: middle; padding: 12px 16px;">
                              Month
                            </th>
                            <th colspan="2" class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                              Input Interest Rate
                            </th>
                            <th rowspan="2" class="text-right" style="font-weight: 600; color: #666; vertical-align: middle; padding: 12px 16px;">
                              Total Interest Rate
                            </th>
                          </tr>
                          <tr style="background-color: #fafafa;">
                            <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                              Choose Interest Type
                            </th>
                            <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                              Input Interest Difference
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, index) in variant.details" :key="index" style="background-color: white;">
                            <td class="text-left" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                              <span style="font-weight: 500; color: #333;">{{ getMonthRangeDisplay(item) }}</span>
                            </td>
                            <td class="text-center" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                              <v-select
                                v-model="item.interestType"
                                :items="['Fixed', 'MRR']"
                                variant="solo"
                                density="compact"
                                hide-details
                                style="max-width: 160px; margin: 0 auto;"
                                @update:model-value="calculateTotalRateForVariant(variant, item)"
                              ></v-select>
                            </td>
                            <td class="text-center" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                              <div class="d-flex align-center justify-center gap-2">
                                <span v-if="item.interestType === 'MRR'" style="color: #666; font-size: 16px;">-</span>
                                <v-text-field
                                  v-model.number="item.inputInterestDifference"
                                  placeholder="Interest Difference %"
                                  variant="outlined"
                                  density="compact"
                                  type="number"
                                  step="0.01"
                                  hide-details
                                  style="max-width: 160px;"
                                  @input="calculateTotalRateForVariant(variant, item)"
                                />
                              </div>
                            </td>
                            <td class="text-right" style="padding: 12px 16px;">
                              <span style="font-weight: 500; color: #333;">{{ item.totalInterestRate }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-card>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>

          <!-- ═══ PERSONAL LOAN: Variant Expansion Panels with Profession×Salary×Tenor Grid ═══ -->
          <div v-if="newInterestRate.productType === 'Personal_Loan'" class="mb-4">
            <div class="d-flex align-center justify-space-between mb-3">
              <label class="field-label">Interest Rate Variants</label>
              <v-btn size="small" color="primary" variant="outlined" @click="addVariant">
                <v-icon start size="small">mdi-plus</v-icon>
                Add Variant
              </v-btn>
            </div>

            <v-expansion-panels variant="accordion" class="variant-panels">
              <v-expansion-panel
                v-for="(variant, variantIndex) in newInterestRate.variants"
                :key="variantIndex"
                elevation="0"
                class="variant-panel"
              >
                <v-expansion-panel-title class="variant-panel-title">
                  <div class="variant-header">
                    <div class="variant-header-left">
                      <div class="variant-number">
                        <span class="variant-number-text">{{ variantIndex + 1 }}</span>
                      </div>
                      <div class="variant-input-wrapper">
                        <v-icon class="variant-input-icon" size="18">mdi-tag-outline</v-icon>
                        <v-text-field
                          v-model="variant.variantName"
                          placeholder="Variant name"
                          variant="plain"
                          density="compact"
                          hide-details
                          class="variant-name-input"
                          @click.stop
                        />
                      </div>
                    </div>
                    <div class="variant-header-right">
                      <v-chip size="small" variant="flat" color="success" class="variant-status-chip">
                        <v-icon start size="12">mdi-check-circle</v-icon>
                        Active
                      </v-chip>
                      <v-btn
                        v-if="newInterestRate.variants.length > 1"
                        class="variant-delete-btn"
                        size="small"
                        variant="tonal"
                        color="error"
                        @click.stop="removeVariant(variantIndex)"
                      >
                        <v-icon size="18">mdi-trash-can-outline</v-icon>
                        <span class="ml-1">Delete</span>
                      </v-btn>
                    </div>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-card elevation="0" style="border: 1px solid #e0e0e0; border-radius: 12px; overflow: hidden; margin-top: 12px;">
                    <v-card-title class="d-flex align-center justify-space-between pa-4" style="background-color: #fafafa;">
                      <span style="font-weight: 600; color: #333;">Personal Loan Interest Rate Table</span>
                      <v-btn size="small" color="primary" variant="outlined" @click="addPersonalLoanMonthForVariant(variantIndex)">
                        <v-icon start size="small">mdi-plus</v-icon>
                        Add Month
                      </v-btn>
                    </v-card-title>

                    <div class="personal-loan-scroll-container">
                      <div class="personal-loan-table-wrapper">
                        <table class="personal-loan-table">
                          <thead>
                            <tr style="background-color: #fafafa;">
                              <th rowspan="2" class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 150px; vertical-align: middle;">
                                Profession
                              </th>
                              <th rowspan="2" class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 180px; vertical-align: middle;">
                                Monthly Income Range
                              </th>
                              <th :colspan="variant.personalLoanMonths.length" class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">
                                Interest Rate by Month
                              </th>
                              <th colspan="2" class="text-center" style="font-weight: 600; color: #666; padding: 8px 16px;">
                                Actions
                              </th>
                            </tr>
                            <tr style="background-color: #fafafa;">
                              <th
                                v-for="(month, index) in variant.personalLoanMonths"
                                :key="`month-${variantIndex}-${index}`"
                                class="text-center"
                                style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 120px;"
                              >
                                <div class="d-flex align-center justify-center gap-2">
                                  <v-text-field
                                    v-model="month.value"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    style="max-width: 80px;"
                                    @input="filterNumericInput($event, month)"
                                    @keypress="validateNumericKeypress"
                                  />
                                  <v-btn
                                    icon
                                    size="x-small"
                                    variant="text"
                                    color="error"
                                    @click="removePersonalLoanMonthForVariant(variantIndex, index)"
                                    v-if="variant.personalLoanMonths.length > 1"
                                  >
                                    <v-icon size="small">mdi-close</v-icon>
                                  </v-btn>
                                </div>
                              </th>
                              <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px; min-width: 80px;">
                                Range
                              </th>
                              <th class="text-center" style="font-weight: 600; color: #666; padding: 8px 16px; min-width: 80px;">
                                Profession
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(profession, profIndex) in variant.personalLoanData" :key="`prof-${variantIndex}-${profIndex}`">
                              <tr
                                v-for="(range, rangeIndex) in profession.salaryRanges"
                                :key="`range-${variantIndex}-${profIndex}-${rangeIndex}`"
                                style="background-color: white;"
                              >
                                <td
                                  v-if="rangeIndex === 0"
                                  :rowspan="profession.salaryRanges.length"
                                  class="text-left"
                                  style="padding: 12px 16px; border-right: 1px solid #e0e0e0; vertical-align: top;"
                                >
                                  <v-combobox
                                    v-model="profession.occupationId"
                                    :items="occupationsData"
                                    item-title="occupationNameTH"
                                    item-value="occupationID"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    placeholder="Select Profession"
                                    style="min-width: 130px;"
                                  />
                                </td>
                                <td class="text-left" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                                  <div class="d-flex align-center gap-2">
                                    <v-text-field
                                      v-model="range.rangeMin"
                                      variant="outlined"
                                      density="compact"
                                      type="number"
                                      hide-details
                                      placeholder="Min"
                                      style="max-width: 100px;"
                                    />
                                    <span style="color: #666;">-</span>
                                    <v-text-field
                                      v-model="range.rangeMax"
                                      variant="outlined"
                                      density="compact"
                                      type="number"
                                      hide-details
                                      placeholder="Max"
                                      style="max-width: 100px;"
                                    />
                                  </div>
                                </td>
                                <td
                                  v-for="(month, monthIndex) in variant.personalLoanMonths"
                                  :key="`rate-${variantIndex}-${profIndex}-${rangeIndex}-${monthIndex}`"
                                  class="text-center"
                                  style="padding: 12px 16px; border-right: 1px solid #e0e0e0;"
                                >
                                  <v-text-field
                                    v-model.number="range.interestRates[monthIndex]"
                                    variant="outlined"
                                    density="compact"
                                    type="number"
                                    step="0.01"
                                    hide-details
                                    suffix="%"
                                    placeholder="0.00"
                                    style="max-width: 100px; margin: 0 auto;"
                                  />
                                </td>
                                <td class="text-center" style="padding: 4px 8px; border-right: 1px solid #e0e0e0;">
                                  <div class="d-flex gap-1 justify-center">
                                    <v-btn
                                      icon
                                      size="x-small"
                                      variant="text"
                                      color="success"
                                      @click="addSalaryRangeForVariant(variantIndex, profIndex)"
                                      v-if="rangeIndex === profession.salaryRanges.length - 1"
                                    >
                                      <v-icon size="18">mdi-plus</v-icon>
                                      <v-tooltip activator="parent" location="top">Add Range</v-tooltip>
                                    </v-btn>
                                    <v-btn
                                      icon
                                      size="x-small"
                                      variant="text"
                                      color="error"
                                      @click="removeSalaryRangeForVariant(variantIndex, profIndex, rangeIndex)"
                                      v-if="profession.salaryRanges.length > 1"
                                    >
                                      <v-icon size="18">mdi-minus</v-icon>
                                      <v-tooltip activator="parent" location="top">Remove Range</v-tooltip>
                                    </v-btn>
                                  </div>
                                </td>
                                <td
                                  v-if="rangeIndex === 0"
                                  :rowspan="profession.salaryRanges.length"
                                  class="text-center"
                                  style="padding: 4px 8px; vertical-align: middle;"
                                >
                                  <v-btn
                                    icon
                                    size="x-small"
                                    variant="text"
                                    color="error"
                                    @click="removeProfessionForVariant(variantIndex, profIndex)"
                                  >
                                    <v-icon size="18">mdi-delete</v-icon>
                                    <v-tooltip activator="parent" location="top">Remove Profession</v-tooltip>
                                  </v-btn>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <v-card-actions class="pa-4" style="background-color: #fafafa;">
                      <v-btn
                        color="primary"
                        variant="outlined"
                        @click="addProfessionForVariant(variantIndex)"
                      >
                        <v-icon start>mdi-plus</v-icon>
                        Add Profession
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <!-- Row 3: Effective Start / End -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Effective_Start (Time Stamp)</label>
              <VueDatePicker
                :model-value="newInterestRate.effectiveStart"
                @update:model-value="(v) => onDocDateChange(newInterestRate, 'effectiveStart', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Effective_End (Time Stamp)</label>
              <VueDatePicker
                :model-value="newInterestRate.effectiveEnd"
                @update:model-value="(v) => onDocDateChange(newInterestRate, 'effectiveEnd', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
          </v-row>

          <!-- Note -->
          <div class="mb-4">
            <label class="field-label">Description</label>
            <v-textarea
              v-model="newInterestRate.note"
              variant="outlined"
              density="comfortable"
              hide-details
              rows="3"
            ></v-textarea>
          </div>

          <!-- Status -->
          <div class="mb-2">
            <label class="field-label">Status</label>
            <v-radio-group v-model="newInterestRate.status" hide-details>
              <v-radio label="Active" value="Active"></v-radio>
              <v-radio label="Inactive" value="Inactive"></v-radio>
            </v-radio-group>
          </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" @click="dialogAddInterestRate = false">Cancel</v-btn>
          <v-btn v-if="canAdd" variant="elevated" color="#1976D2" @click="saveNewInterestRate">
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Interest Rate Dialog -->
    <v-dialog v-model="dialogEditInterestRate" persistent max-width="680px">
      <v-card class="add-doc-dialog-card">
        <div class="add-doc-dialog-header">
          <h2>Edit Interest Rate</h2>
        </div>
        <v-card-text class="pa-6">
          <!-- Account Code (readonly) / Name (editable) — Deposit -->
          <v-row v-if="editInterestRateItem.productCategory === 'Deposit'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Account_code</label>
              <v-text-field v-model="editInterestRateItem.accountCode" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Account_name <span style="color: red;">*</span></label>
              <v-text-field
                v-model="editInterestRateItem.accountName"
                :rules="[v => !!v || 'Account name is required']"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mb-4">
            <v-col cols="6">
              <label class="field-label">Effective_Start (Time Stamp)</label>
              <VueDatePicker
                :model-value="editInterestRateItem.effectiveStart"
                @update:model-value="(v) => onDocDateChange(editInterestRateItem, 'effectiveStart', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
            <v-col cols="6">
              <label class="field-label">Effective_End (Time Stamp)</label>
              <VueDatePicker
                :model-value="editInterestRateItem.effectiveEnd"
                @update:model-value="(v) => onDocDateChange(editInterestRateItem, 'effectiveEnd', v)"
                placeholder="Select date & time"
                is24
                :enable-time-picker="true"
                :format="'dd/MM/yyyy HH:mm'"
                model-type="yyyy-MM-dd'T'HH:mm"
                text-input
                :teleport="true"
                :ui="{ menu: 'side-by-side-datetime-menu' }"
                :max-time="{ hours: 23, minutes: 59 }"
                :start-time="{ hours: 0, minutes: 0 }"
                no-hours-overlay
                no-minutes-overlay
                class="custom-datetime-picker"
              >
                <template #time-picker="{ time, updateTime }">
                  <NonLoopTimePicker :time="time" :update-time="updateTime" />
                </template>
                <template #action-row="{ selectDate }">
                  <div class="dp-ok-action-row">
                    <button type="button" class="dp-ok-btn" @click="selectDate">Ok</button>
                  </div>
                </template>
              </VueDatePicker>
            </v-col>
          </v-row>
          <div class="mb-4">
            <label class="field-label">Description</label>
            <v-textarea
              v-model="editInterestRateItem.note"
              variant="outlined"
              density="comfortable"
              hide-details
              rows="3"
            ></v-textarea>
          </div>

          <!-- Status -->
          <div class="mb-2">
            <label class="field-label">Status</label>
            <v-radio-group v-model="editInterestRateItem.status" hide-details>
              <v-radio label="Active" value="Active"></v-radio>
              <v-radio label="Inactive" value="Inactive"></v-radio>
            </v-radio-group>
          </div>

          <!-- Meta Category / SubCategory (Deposit) -->
          <v-row v-if="editInterestRateItem.productCategory === 'Deposit'" class="mb-2">
            <v-col cols="12" md="6">
              <label class="field-label">Category</label>
              <v-select
                v-model="editInterestRateItem.categoryID"
                :items="metaCategoryOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                placeholder="Select Category"
                @update:model-value="onEditInterestCategoryChange"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Subcategory</label>
              <v-select
                v-model="editInterestRateItem.subCategoryID"
                :items="metaSubCategoryOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                placeholder="Select Subcategory"
                :disabled="!editInterestRateItem.categoryID"
              ></v-select>
            </v-col>
          </v-row>

          <div v-if="editInterestRateItem.productCategory === 'Deposit'" class="mb-2">
            <div class="customer-scope-toggle">
              <div>
                <div class="customer-scope-title">{{ getCustomerScopeToggleTitle(editInterestRateItem.isNewUser) }}</div>
                <div class="customer-scope-description">{{ getCustomerScopeDescription(editInterestRateItem.isNewUser) }}</div>
              </div>
              <v-switch v-model="editInterestRateItem.isNewUser" color="primary" inset hide-details />
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" @click="dialogEditInterestRate = false">Cancel</v-btn>
          <v-btn v-if="canEdit" variant="elevated" color="#1976D2" @click="saveEditInterestRate">
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Interest Rate Dialog -->
    <v-dialog v-model="dialogViewInterestRate" max-width="960px">
      <v-card class="view-doc-dialog-card">
        <div class="add-doc-dialog-header">
          <h2>View Interest Rate</h2>
        </div>
        <v-card-text class="pa-6">
          <!-- Account Code / Name (readonly, Deposit) -->
          <v-row v-if="viewInterestRateItem.productCategory === 'Deposit'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Account_code</label>
              <v-text-field v-model="viewInterestRateItem.accountCode" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Account_name</label>
              <v-text-field v-model="viewInterestRateItem.accountName" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
          </v-row>
          <!-- Row 1: Product Category / Type / Subtype (readonly) -->
          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <label class="field-label">Product_category <span style="color: red;">*</span></label>
              <v-text-field v-model="viewInterestRateItem.productCategory" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="field-label">Product_type <span style="color: red;">*</span></label>
              <v-text-field v-model="viewInterestRateItem.productType" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="field-label">Product_subtype <span style="color: red;">*</span></label>
              <v-text-field v-model="viewInterestRateItem.productSubtype" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
          </v-row>

          <div v-if="viewInterestRateItem.productCategory === 'Deposit'" class="mb-4">
            <label class="field-label">Customer Scope</label>
            <v-chip color="#4B2869" size="small" variant="outlined" class="customer-scope-chip">
              {{ getCustomerScopeText(viewInterestRateItem.isNewUser) }}
            </v-chip>
          </div>

          <!-- Meta Category / SubCategory (readonly, Deposit only) -->
          <v-row v-if="viewInterestRateItem.productCategory === 'Deposit'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Category</label>
              <v-text-field v-model="viewInterestRateItem.categoryName" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Subcategory</label>
              <v-text-field v-model="viewInterestRateItem.subCategoryName" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
          </v-row>

          <!-- Row 2: Calculation Method / Interest Payment Frequency (readonly, Deposit only) -->
          <v-row v-if="viewInterestRateItem.productCategory !== 'Loan'" class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Calculation Method</label>
              <v-text-field v-model="viewInterestRateItem.calculationMethod" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Interest Payment Frequency</label>
              <v-text-field v-model="viewInterestRateItem.paymentFrequency" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
          </v-row>

          <!-- Interest Rate Tier Table (readonly, Deposit only) -->
          <div v-if="viewInterestRateItem.productCategory === 'Deposit' || !viewInterestRateItem.productCategory" class="mb-4">
            <div class="tier-table-wrapper">
              <table class="tier-table">
                <thead>
                  <tr>
                    <th>Tier Name</th>
                    <th>Lower Amount</th>
                    <th>Upper Amount</th>
                    <th>Interest Rate</th>
                    <th>Min Day</th>
                    <th>Max Day</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!viewInterestRateItem.tiers || viewInterestRateItem.tiers.length === 0">
                    <td colspan="6" class="text-center text-grey py-3" style="font-size: 13px;">No tiers.</td>
                  </tr>
                  <tr v-for="(tier, index) in (viewInterestRateItem.tiers || [])" :key="index">
                    <td>{{ tier.tierName || '-' }}</td>
                    <td>{{ formatAmountDisplay(tier.lowerAmount) }}</td>
                    <td>{{ formatAmountDisplay(tier.upperAmount) }}</td>
                    <td>{{ tier.interestRate !== null && tier.interestRate !== undefined ? formatPercentDisplay(tier.interestRate) + '%' : '-' }}</td>
                    <td>{{ tier.minDay || '-' }}</td>
                    <td>{{ tier.maxDay || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ═══ HOME LOAN: View MRR Rate + Variant Panels (readonly) ═══ -->
          <div v-if="viewInterestRateItem.productCategory === 'Loan' && viewInterestRateItem.productType === 'Home_Loan'" class="mb-4">
            <div class="mb-4">
              <div style="background-color: #e3f2fd; padding: 12px 20px; border-radius: 8px; border: 1px solid #90caf9; display: inline-block;">
                <div style="color: #1976d2; font-size: 14px; font-weight: 500;">MRR Rate = {{ formatPercentDisplay(viewInterestRateItem.mrrRate) }}%</div>
              </div>
            </div>
            <label class="field-label mb-2">Interest Rate Variants</label>
            <v-expansion-panels variant="accordion" class="variant-panels">
              <v-expansion-panel
                v-for="(variant, vIdx) in viewInterestRateItem.variants"
                :key="vIdx"
                elevation="0"
                class="variant-panel"
              >
                <v-expansion-panel-title class="variant-panel-title">
                  <div class="variant-header">
                    <div class="variant-header-left">
                      <div class="variant-number">
                        <span class="variant-number-text">{{ vIdx + 1 }}</span>
                      </div>
                      <span style="font-weight: 500; color: #333; margin-left: 8px;">{{ variant.variantName || 'Variant ' + (vIdx + 1) }}</span>
                      <span style="color: #666; font-size: 12px; margin-left: 12px;">
                        {{ installmentTypes.find(function(t) { return t.value === variant.installmentType; })?.name || variant.installmentType }}
                      </span>
                    </div>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-card elevation="0" style="border-radius: 12px; overflow: hidden; margin-top: 12px;">
                    <v-table density="comfortable" class="interest-rate-table">
                      <thead>
                        <tr style="background-color: #fafafa;">
                          <th class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px;">Month</th>
                          <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">Interest Type</th>
                          <th class="text-center" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 8px 16px;">Interest Difference</th>
                          <th class="text-right" style="font-weight: 600; color: #666; padding: 12px 16px;">Total Interest Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(detail, dIdx) in variant.details" :key="dIdx" style="background-color: white;">
                          <td class="text-left" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                            <span style="font-weight: 500; color: #333;">{{ getMonthRangeDisplay(detail) }}</span>
                          </td>
                          <td class="text-center" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">{{ detail.interestType }}</td>
                          <td class="text-center" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                            <span v-if="detail.interestType === 'MRR'">- </span>{{ formatPercentDisplay(detail.inputInterestDifference) }}%
                          </td>
                          <td class="text-right" style="padding: 12px 16px;">{{ formatPercentDisplay(detail.totalInterestRate) }}%</td>
                        </tr>
                        <tr v-if="!variant.details || !variant.details.length">
                          <td colspan="4" class="text-center text-grey pa-4">No detail rows</td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-card>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <div v-if="!viewInterestRateItem.variants.length" class="text-center text-grey py-4" style="font-size: 13px; border: 1px solid #e0e0e0; border-radius: 8px;">
              No variant data available.
            </div>
          </div>

          <!-- ═══ PERSONAL LOAN: View Variant Panels (readonly) ═══ -->
          <div v-if="viewInterestRateItem.productCategory === 'Loan' && viewInterestRateItem.productType === 'Personal_Loan'" class="mb-4">
            <label class="field-label mb-2">Interest Rate Variants</label>
            <v-expansion-panels variant="accordion" class="variant-panels">
              <v-expansion-panel
                v-for="(variant, vIdx) in viewInterestRateItem.variants"
                :key="vIdx"
                elevation="0"
                class="variant-panel"
              >
                <v-expansion-panel-title class="variant-panel-title">
                  <div class="variant-header">
                    <div class="variant-header-left">
                      <div class="variant-number">
                        <span class="variant-number-text">{{ vIdx + 1 }}</span>
                      </div>
                      <span style="font-weight: 500; color: #333; margin-left: 8px;">{{ variant.variantName || 'Variant ' + (vIdx + 1) }}</span>
                    </div>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-card elevation="0" style="border: 1px solid #e0e0e0; border-radius: 12px; overflow: hidden; margin-top: 12px;">
                    <v-card-title class="pa-4" style="background-color: #fafafa;">
                      <span style="font-weight: 600; color: #333;">Personal Loan Interest Rate Table</span>
                    </v-card-title>
                    <div class="personal-loan-scroll-container">
                      <div class="personal-loan-table-wrapper">
                        <table class="personal-loan-table">
                          <thead>
                            <tr style="background-color: #fafafa;">
                              <th class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 150px;">Profession</th>
                              <th class="text-left" style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 180px;">Monthly Income Range</th>
                              <th
                                v-for="(month, mIdx) in variant.personalLoanMonths"
                                :key="'vm-' + vIdx + '-' + mIdx"
                                class="text-center"
                                style="font-weight: 600; color: #666; border-right: 1px solid #e0e0e0; padding: 12px 16px; min-width: 100px;"
                              >
                                {{ month.value }} months
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(prof, pIdx) in variant.personalLoanData" :key="'vp-' + vIdx + '-' + pIdx">
                              <tr v-for="(range, rIdx) in prof.salaryRanges" :key="'vr-' + vIdx + '-' + pIdx + '-' + rIdx" style="background-color: white;">
                                <td
                                  v-if="rIdx === 0"
                                  :rowspan="prof.salaryRanges.length"
                                  class="text-left"
                                  style="padding: 12px 16px; border-right: 1px solid #e0e0e0; vertical-align: top;"
                                >
                                  {{ getOccupationName(prof.occupationId) }}
                                </td>
                                <td class="text-left" style="padding: 12px 16px; border-right: 1px solid #e0e0e0;">
                                  {{ formatSalaryRange(range.rangeMin, range.rangeMax) }}
                                </td>
                                <td
                                  v-for="(rate, riIdx) in range.interestRates"
                                  :key="'vri-' + vIdx + '-' + pIdx + '-' + rIdx + '-' + riIdx"
                                  class="text-center"
                                  style="padding: 12px 16px; border-right: 1px solid #e0e0e0;"
                                >
                                  {{ formatPercentDisplay(rate) }}%
                                </td>
                              </tr>
                            </template>
                            <tr v-if="!variant.personalLoanData || !variant.personalLoanData.length">
                              <td :colspan="2 + (variant.personalLoanMonths || []).length" class="text-center text-grey pa-4">No data available</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </v-card>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <div v-if="!viewInterestRateItem.variants.length" class="text-center text-grey py-4" style="font-size: 13px; border: 1px solid #e0e0e0; border-radius: 8px;">
              No variant data available.
            </div>
          </div>

          <!-- Effective Start / End (readonly) -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <label class="field-label">Effective_Start (Time Stamp)</label>
              <v-text-field v-model="viewInterestRateItem.effectiveStartDate" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="field-label">Effective_End (Time Stamp)</label>
              <v-text-field v-model="viewInterestRateItem.effectiveEndDate" variant="outlined" density="comfortable" hide-details readonly></v-text-field>
            </v-col>
          </v-row>

          <!-- Note (readonly) -->
          <div class="mb-4">
            <label class="field-label">Description</label>
            <v-textarea v-model="viewInterestRateItem.note" variant="outlined" density="comfortable" hide-details readonly rows="3"></v-textarea>
          </div>

          <!-- Status (readonly) -->
          <div class="mb-2">
            <label class="field-label">Status</label>
            <v-radio-group v-model="viewInterestRateItem.status" hide-details disabled>
              <v-radio label="Active" value="Active"></v-radio>
              <v-radio label="Inactive" value="Inactive"></v-radio>
            </v-radio-group>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="dialogViewInterestRate = false">CLOSE</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ════════ Meta Category Management Dialog ════════ -->
    <v-dialog v-model="categoriesDialog" max-width="1200px">
      <v-card class="view-doc-dialog-card">
        <div class="add-doc-dialog-header d-flex align-center justify-space-between">
          <h2>Category Management (Meta)</h2>
          <v-btn v-if="canAdd" variant="elevated" color="#1976D2" size="small" @click="openCategoryDialog()">
            <v-icon start>mdi-plus</v-icon>
            Add Category
          </v-btn>
        </div>
        <v-card-text class="pa-6">
          <v-data-table :headers="categoryHeaders" :items="metaAccountCategories" :loading="loadingMetaCategories" density="comfortable" :items-per-page="10">
            <template v-slot:item.activeStatus="{ item }">
              <v-chip :color="item.activeStatus ? 'success' : 'error'" size="small" variant="outlined">
                {{ item.activeStatus ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>
            <template v-slot:item.subCategoriesCount="{ item }">
              <v-chip color="info" size="small" variant="outlined">{{ item.subCategoriesCount || 0 }} Subcategories</v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="secondary" class="action-btn" @click="viewSubCategories(item)">
                  <v-icon size="16">mdi-view-list</v-icon>
                  <v-tooltip activator="parent" location="top">View Subcategories</v-tooltip>
                </v-btn>
                <v-btn size="small" variant="outlined" color="primary" class="action-btn" @click="openCategoryDialog(item)">
                  <v-icon size="16">{{ canEdit ? 'mdi-pencil' : 'mdi-eye-outline' }}</v-icon>
                  <v-tooltip activator="parent" location="top">{{ canEdit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="canDelete" size="small" variant="outlined" color="error" class="action-btn" @click="confirmDeleteCategory(item)">
                  <v-icon size="16">mdi-delete</v-icon>
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="closeCategoriesDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ════════ Meta Category Create/Edit Dialog ════════ -->
    <v-dialog v-model="categoryDialog" max-width="800px">
      <v-card class="view-doc-dialog-card">
        <div class="add-doc-dialog-header">
          <h2>{{ categoryEditMode ? (canEdit ? 'Edit Category' : 'View Category') : 'Create Category' }}</h2>
        </div>
        <v-card-text class="pa-6">
          <v-form ref="categoryForm" v-model="categoryFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <label class="field-label">Category Code <span style="color: red;">*</span></label>
                <v-text-field v-model="categoryFormData.categoryCode" variant="outlined" density="comfortable" hide-details="auto" :rules="[v => !!v || 'Required']"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">Category Name <span style="color: red;">*</span></label>
                <v-text-field v-model="categoryFormData.categoryName" variant="outlined" density="comfortable" hide-details="auto" :rules="[v => !!v || 'Required']"></v-text-field>
              </v-col>
              <v-col cols="12">
                <label class="field-label">Description</label>
                <v-textarea v-model="categoryFormData.description" variant="outlined" density="comfortable" rows="3" hide-details="auto"></v-textarea>
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">Display Order</label>
                <v-text-field v-model.number="categoryFormData.displayOrder" type="number" variant="outlined" density="comfortable" hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-checkbox v-model="categoryFormData.activeStatus" label="Active" color="primary" hide-details></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" @click="closeCategoryDialog">Cancel</v-btn>
          <v-btn v-if="categoryEditMode ? canEdit : canAdd" variant="elevated" color="#1976D2" :loading="savingMetaCategory" :disabled="!categoryFormValid" @click="saveCategory">
            {{ categoryEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ════════ Meta Subcategories View Dialog ════════ -->
    <v-dialog v-model="subCategoriesDialog" max-width="1000px">
      <v-card class="view-doc-dialog-card">
        <div class="add-doc-dialog-header d-flex align-center justify-space-between">
          <h2>Subcategories — {{ selectedCategory ? selectedCategory.categoryName : '' }}</h2>
          <v-btn v-if="canAdd" variant="elevated" color="#1976D2" size="small" @click="openSubCategoryDialog()">
            <v-icon start>mdi-plus</v-icon>
            Add Subcategory
          </v-btn>
        </div>
        <v-card-text class="pa-6">
          <v-data-table :headers="subCategoryHeaders" :items="categorySubCategories" :loading="loadingMetaSubCategories" density="comfortable">
            <template v-slot:item.activeStatus="{ item }">
              <v-chip :color="item.activeStatus ? 'success' : 'error'" size="small" variant="outlined">
                {{ item.activeStatus ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="primary" class="action-btn" @click="openSubCategoryDialog(item)">
                  <v-icon size="16">{{ canEdit ? 'mdi-pencil' : 'mdi-eye-outline' }}</v-icon>
                  <v-tooltip activator="parent" location="top">{{ canEdit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="canDelete" size="small" variant="outlined" color="error" class="action-btn" @click="confirmDeleteSubCategory(item)">
                  <v-icon size="16">mdi-delete</v-icon>
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="closeSubCategoriesDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ════════ Meta Subcategory Create/Edit Dialog ════════ -->
    <v-dialog v-model="subCategoryDialog" max-width="500px">
      <v-card class="view-doc-dialog-card">
        <div class="add-doc-dialog-header">
          <h2>{{ subCategoryEditMode ? (canEdit ? 'Edit Subcategory' : 'View Subcategory') : 'Create Subcategory' }}</h2>
        </div>
        <v-card-text class="pa-6">
          <v-form ref="subCategoryForm" v-model="subCategoryFormValid">
            <v-row>
              <v-col cols="12">
                <label class="field-label">Subcategory Code <span style="color: red;">*</span></label>
                <v-text-field v-model="subCategoryFormData.subCategoryCode" variant="outlined" density="comfortable" hide-details="auto" :rules="[v => !!v || 'Required']"></v-text-field>
              </v-col>
              <v-col cols="12">
                <label class="field-label">Subcategory Name <span style="color: red;">*</span></label>
                <v-text-field v-model="subCategoryFormData.subCategoryName" variant="outlined" density="comfortable" hide-details="auto" :rules="[v => !!v || 'Required']"></v-text-field>
              </v-col>
              <v-col cols="12">
                <label class="field-label">Description</label>
                <v-textarea v-model="subCategoryFormData.description" variant="outlined" density="comfortable" rows="3" hide-details="auto"></v-textarea>
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">Display Order</label>
                <v-text-field v-model.number="subCategoryFormData.displayOrder" type="number" variant="outlined" density="comfortable" hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-checkbox v-model="subCategoryFormData.activeStatus" label="Active" color="primary" hide-details></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="elevated" color="#f0960a" class="text-white" @click="closeSubCategoryDialog">Cancel</v-btn>
          <v-btn v-if="subCategoryEditMode ? canEdit : canAdd" variant="elevated" color="#1976D2" :loading="savingMetaSubCategory" :disabled="!subCategoryFormValid" @click="saveSubCategory">
            {{ subCategoryEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { h } from 'vue';
import checkPermissionTwo from '@/api/function.js';
import ExcelJS from 'exceljs';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import generalImg from '@/assets/images/Group 11.svg';
import maxCharacterImg from '@/assets/images/Group 12.svg';
import customSplitImg from '@/assets/images/Group 13.svg';
import articleImg from '@/assets/images/Group 13.svg';
import headerAllChunkImg from '@/assets/images/Group 11.svg';
import headerFirstChunkImg from '@/assets/images/Group 12.svg';
import wordImageImg from '@/assets/images/Group 12.svg';
import pdfImageImg from '@/assets/images/Group 12.svg';

const NonLoopTimePicker = {
  name: 'NonLoopTimePicker',
  props: {
    time: { type: Object, required: true },
    updateTime: { type: Function, required: true },
  },
  methods: {
    getPart(value) {
      const rawValue = Array.isArray(value) ? value[0] : value;
      const numberValue = Number(rawValue);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    formatPart(value) {
      return String(this.getPart(value)).padStart(2, '0');
    },
    getOptions(isHours) {
      const max = isHours ? 23 : 59;
      return Array.from({ length: max + 1 }, (_, value) => value);
    },
    renderPart(label, value, isHours) {
      const current = this.getPart(value);
      return h('div', { class: 'non-loop-time-column', role: 'listbox', 'aria-label': label },
        this.getOptions(isHours).map((option) => h('button', {
          key: option,
          type: 'button',
          class: ['non-loop-time-option', { 'non-loop-time-option-active': option === current }],
          role: 'option',
          'aria-selected': option === current ? 'true' : 'false',
          onClick: () => this.updateTime(option, isHours),
        }, this.formatPart(option)))
      );
    },
    renderColumn(headerText, ariaLabel, value, isHours) {
      return h('div', { class: 'non-loop-time-group' }, [
        h('div', { class: 'non-loop-time-header' }, headerText),
        this.renderPart(ariaLabel, value, isHours),
      ]);
    },
  },
  render() {
    return h('div', { class: 'non-loop-time-picker' }, [
      this.renderColumn('HH', 'hours', this.time.hours, true),
      h('div', { class: 'non-loop-time-separator' }, ':'),
      this.renderColumn('mm', 'minutes', this.time.minutes, false),
    ]);
  },
};

const DEFAULT_PERSONAL_LOAN_MONTH_VALUES = [12, 24, 36, 48, 60];

const createDefaultPersonalLoanMonths = () =>
  DEFAULT_PERSONAL_LOAN_MONTH_VALUES.map((value) => ({ value }));

const createDefaultPersonalLoanData = (monthsLength = DEFAULT_PERSONAL_LOAN_MONTH_VALUES.length) => [
  {
    occupationId: null,
    salaryRanges: [
      {
        rangeMin: null,
        rangeMax: null,
        interestRates: [],
      },
    ],
  },
];

const createDefaultHomeLoanDetails = () => [
  {
    monthRangeStart: 1,
    monthRangeEnd: 12,
    interestType: 'Fixed',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
  {
    monthRangeStart: 13,
    monthRangeEnd: 24,
    interestType: 'MRR',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
  {
    monthRangeStart: 25,
    monthRangeEnd: 36,
    interestType: 'MRR',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
  {
    monthRangeStart: 37,
    monthRangeEnd: null,
    interestType: 'MRR',
    inputInterestDifference: 0,
    totalInterestRate: '0.000%',
  },
];

export default {
  name: 'DocumentControl',
  components: {
    VueDatePicker,
    NonLoopTimePicker,
  },
  inject: [
    'flagNav',
    'FontColorTitle',
    'SubNavbarBackGround',
    'Username',
    'RoleId',
  ],
  mixins: [checkPermissionTwo],
  data: () => ({
    today: null,
    activeTab: 'document',
    loadingTable: false,
    refreshIntervalId: null,
    tableData: [],
    totalItems: 0,
    itemsPerPage: -1,
    options: {
      page: 1,
      itemsPerPage: -1,
      sortBy: [],
      sortDesc: [],
    },
    vectorDBName: '',
    productCategory: '',
    subTitle: '',
    productSubtype: '',
    itemID: '',
    parentDocNameEN: '',
    parentDocNameTH: '',

    // Interest Rate Control
    interestRateData: [],
    dialogAddInterestRate: false,
    // When adding a version to an existing account, lock the accountCode field
    lockAccountCode: false,
    newInterestRate: {
      productCategory: '',
      productType: '',
      productSubtype: '',
      calculationMethod: '',
      paymentFrequency: '',
      tiers: [],
      variants: [],
      effectiveStart: '',
      effectiveEnd: '',
      note: '',
      status: 'Active',
      // SQL sync fields - Deposit
      accountCode: '',
      accountName: '',
      categoryID: null,
      subCategoryID: null,
      // SQL sync fields - Loan
      interestRateName: '',
      interestRateType: '',
      mrrRate: 0,
      loanCategoryID: null,
      isNewUser: false,
    },
    // Loan variant management
    occupationsData: [],
    categoriesData: [],
    loadingCategories: false,
    installmentTypes: [
      { value: 'Normal', name: 'ค่างวดคงที่ตลอดอายุสินเชื่อ' },
      { value: 'LowInstallment3Years', name: 'ค่างวดการผ่อนต่ำ 3 ปีแรก ตามดอกเบี้ยปกติ' },
      { value: 'FrontLowPerMillion', name: 'ค่างวดการผ่อนหน้าต่ำ ล้านละ' },
    ],
    dialogEditInterestRate: false,
    editInterestRateItem: {
      version: '',
      productCategory: '',
      effectiveStart: '',
      effectiveEnd: '',
      note: '',
      status: 'Active',
      isNewUser: false,
      categoryID: null,
      subCategoryID: null,
    },

    // Interest Rate dropdown options
    productCategoryOptions: ['Deposit', 'Loan'],
    productTypeMap: {
      Deposit: ['Saving', 'Time_Deposit', 'Tax_Free'],
      Loan: ['Home_Loan', 'Personal_Loan'],
    },
    productSubtypeMap: {
      'Saving': ['THB', 'Foreign_Currency_Deposit'],
      'Time_Deposit': ['THB', 'Foreign_Currency_Deposit'],
      'Tax_Free': ['THB', 'Foreign_Currency_Deposit'],
      'Home_Loan': ['New/Default', 'Refinance', 'Home_for_Cash'],
      'Personal_Loan': ['New/Default'],
    },
    calculationMethodOptions: [
      { title: 'Tier Rate', value: 'TierRate' },
      { title: 'Split Rate', value: 'SplitRate' },
      { title: 'Fixed Rate', value: 'FixedRate' },
    ],
    paymentFrequencyOptions: ['Half Year', 'Monthly', 'Maturity Date'],
    dialogViewInterestRate: false,
    viewInterestRateItem: {
      version: '',
      productCategory: '',
      productType: '',
      productSubtype: '',
      calculationMethod: '',
      paymentFrequency: '',
      tiers: [],
      mrrRate: 0,
      variants: [],
      effectiveStartDate: '',
      effectiveEndDate: '',
      note: '',
      status: '',
      isNewUser: false,
      categoryID: null,
      subCategoryID: null,
      categoryName: '-',
      subCategoryName: '-',
    },

    // Add New Document dialog
    dialogAddDoc: false,
    newDoc: {
      fileName: '',
      file: null,
      effectiveStart: '',
      effectiveEnd: '',
      json: '',
      webLink: '',
      universalLink: '',
      channels: [],
      channelOtherEnabled: false,
      channelOther: '',
      note: '',
      status: 'Active',
      vectorType: null,
      modelType: null,
      customCharacter: null,
      maxLimitedCharacter: null,
      chunkOverlapChars: 0,
      refineWithLLM: false,
    },
    documentRules: {
      fileRequired: (value) => !!value || 'Document file is required',
    },
    filterDDType: [],
    filterdatavectortype: [],
    datavectortype: [
      { id: 'General',   name: 'General',   value: 1 },
      { id: 'Article',   name: 'Article',   value: 2 },
      { id: 'PriceList', name: 'PriceList', value: 3 },
      { id: 'MD',        name: 'MD',        value: 4 },
    ],
    DDType: [
      { id: 'General',          name: 'General',          value: 1, image: generalImg },
      { id: 'MaxCharacter',     name: 'MaxCharacter',     value: 2, image: maxCharacterImg },
      { id: 'CustomSplit',      name: 'CustomSplit',      value: 3, image: customSplitImg },
      { id: 'Article',          name: 'Article',          value: 4, image: articleImg },
      { id: 'HeaderAllChunk',   name: 'HeaderAllChunk',   value: 5, image: headerAllChunkImg },
      { id: 'HeaderFirstChunk', name: 'HeaderFirstChunk', value: 6, image: headerFirstChunkImg },
      { id: 'WordImage',        name: 'WordImage',        value: 7, image: wordImageImg },
      { id: 'PDFImage',         name: 'PDFImage',         value: 8, image: pdfImageImg },
      { id: 'Auto',             name: 'Auto',             value: 9, image: generalImg },
      { id: 'MDCustomSplit',    name: 'MD Custom Split',  value: 10, image: customSplitImg },
      { id: 'AutoRewritter',    name: 'Auto Rewritter',   value: 11, image: generalImg },
    ],

    // View Document dialog
    dialogViewDoc: false,
    viewDoc: {
      itemName: '',
      version: '',
      headId: null,
      chunks: [],
      json: '',
      webLink: '',
      universalLink: '',
      channels: [],
      channelOtherEnabled: false,
      channelOther: '',
      note: '',
      status: 'Active',
    },

    // Edit Document dialog
    dialogEditDoc: false,
    editDoc: {
      itemName: '',
      version: '',
      effectiveStart: '',
      effectiveEnd: '',
      note: '',
      status: 'Active',
    },
    dialogEditChunk: false,
    savingChunk: false,
    editChunk: {
      id: null,
      text: '',
      originalText: '',
    },

    // ── Meta Category / SubCategory management ──
    categoriesDialog: false,
    categoryDialog: false,
    subCategoriesDialog: false,
    subCategoryDialog: false,
    categoryEditMode: false,
    subCategoryEditMode: false,
    loadingMetaCategories: false,
    loadingMetaSubCategories: false,
    savingMetaCategory: false,
    savingMetaSubCategory: false,
    categoryFormValid: false,
    subCategoryFormValid: false,
    metaAccountCategories: [],
    categorySubCategories: [],
    selectedCategory: null,
    categoryFormData: {
      categoryID: 0,
      categoryCode: '',
      categoryName: '',
      description: '',
      displayOrder: 0,
      isActive: true,
      activeStatus: true,
    },
    subCategoryFormData: {
      subCategoryID: 0,
      categoryID: 0,
      subCategoryCode: '',
      subCategoryName: '',
      description: '',
      displayOrder: 0,
      isActive: true,
      activeStatus: true,
    },
    categoryHeaders: [
      { title: 'Code', key: 'categoryCode' },
      { title: 'Name', key: 'categoryName' },
      { title: 'Description', key: 'description' },
      { title: 'Order', key: 'displayOrder' },
      { title: 'Subcategories', key: 'subCategoriesCount' },
      { title: 'Status', key: 'activeStatus' },
      { title: 'Actions', key: 'actions', sortable: false },
    ],
    subCategoryHeaders: [
      { title: 'Code', key: 'subCategoryCode' },
      { title: 'Name', key: 'subCategoryName' },
      { title: 'Description', key: 'description' },
      { title: 'Order', key: 'displayOrder' },
      { title: 'Status', key: 'activeStatus' },
      { title: 'Actions', key: 'actions', sortable: false },
    ],
    // Options for the Add/Edit Interest Rate form category selects (Meta)
    metaCategoryOptions: [],
    metaSubCategoryOptions: [],
  }),
  computed: {
    // #720: .docx / .pdf for now · methods that take plain text (General 1–3, Article 4, Auto 9, MD Custom Split 10)
    refineFileSupported() {
      return /\.(docx|pdf)$/i.test(this.newDoc.fileName || '');
    },
    supportsRefine() {
      return this.refineFileSupported && [1, 2, 3, 4, 9, 10].includes(this.newDoc.modelType);
    },
    refineHint() {
      if (!this.newDoc.file) return 'กรุณาเลือกไฟล์';
      if (!this.refineFileSupported) return 'ใช้ได้เฉพาะไฟล์ .docx และ .pdf (ช่วงทดสอบ)';
      if (this.newDoc.modelType == null) return 'เลือก Processing Method ก่อน';
      if (!this.supportsRefine) return 'Processing Method นี้ยังไม่รองรับ';
      return 'ระบบจะใช้ LLM จัดรูปแบบย่อหน้า หัวข้อ และตารางให้เหมาะกับ Vector Type / Processing Method ที่เลือก และแปลงเลขไทยเป็นเลขอารบิกก่อนตัด chunk โดยไม่เปลี่ยนแปลงเนื้อหา ตรวจดูผลลัพธ์ได้ที่ปุ่ม View เมื่อสถานะเป็น Completed';
    },
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('DocumentControl');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('DocumentControl'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('DocumentControl'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('DocumentControl'));
    },
    headers() {
      return [
            { title: 'Actions', align: 'center', sortable: false, width: '180px', class: 'sticky-action' },
        { title: 'Version', align: 'center', sortable: true, width: '100px' },
        { title: 'Status', align: 'center', sortable: false, width: '100px' },
        { title: 'Status Progress', align: 'center', sortable: false, width: '140px' },
        { title: 'File_name', value: 'fileName', align: 'left', sortable: true, width: '200px' },
        { title: 'Effective_start_date', value: 'effectiveStartDate', align: 'left', sortable: true, width: '170px' },
        { title: 'Effective_end_date', value: 'effectiveEndDate', align: 'left', sortable: true, width: '170px' },
        { title: 'Universal link', value: 'universalLink', align: 'left', sortable: false, width: '150px' },
        { title: 'Web_link', value: 'webLink', align: 'left', sortable: false, width: '150px' },
        { title: 'JSON', value: 'json', align: 'left', sortable: false, width: '100px' },
        { title: 'Channel_availability', value: 'channelAvailability', align: 'left', sortable: false, width: '180px' },
        { title: 'Added_by', value: 'addedBy', align: 'left', sortable: false, width: '130px' },
        { title: 'Added_date', value: 'addedDate', align: 'left', sortable: true, width: '160px' },
        { title: 'Last_Update_By', value: 'updateBy', align: 'left', sortable: false, width: '140px' },
        { title: 'Last_Update_Date', value: 'updatedDate', align: 'left', sortable: true, width: '160px' },
        { title: 'Note', value: 'note', align: 'left', sortable: false, width: '150px' },
      ];
    },
    showInterestRateControl() {
      return this.productCategory === 'Deposit' || this.productCategory === 'Loan';
    },
    interestRateHeaders() {
      return [
        { title: 'Actions', align: 'center', sortable: false, width: '130px', class: 'sticky-action' },
        { title: 'Version', align: 'center', sortable: true, width: '100px' },
        { title: 'Status', align: 'center', sortable: false, width: '100px' },
        { title: 'Account Name', value: 'accountName', align: 'left', sortable: true, width: '180px' },
        ...(this.productCategory === 'Deposit' ? [{ title: 'Customer Scope', align: 'center', sortable: false, width: '160px' }] : []),
        { title: 'Effective_start_date', value: 'effectiveStartDate', align: 'left', sortable: true, width: '170px' },
        { title: 'Effective_end_date', value: 'effectiveEndDate', align: 'left', sortable: true, width: '170px' },
        { title: 'Added_by', value: 'addedBy', align: 'left', sortable: false, width: '130px' },
        { title: 'Added_date', value: 'addedDate', align: 'left', sortable: true, width: '160px' },
        { title: 'Last_Update_By', value: 'updateBy', align: 'left', sortable: false, width: '140px' },
        { title: 'Last_Update_Date', value: 'updatedDate', align: 'left', sortable: true, width: '160px' },
        { title: 'Description', value: 'note', align: 'left', sortable: false, width: '150px' },
      ];
    },
    // Group interest-rate rows into one table per accountCode (the metadoc is the parent group).
    // accountCode is the shared group key (header); accountName is shown per-row since versions may differ.
    // Loan / legacy rows with no accountCode fall into a single 'Default' group.
    interestRateGroups() {
      const groups = {};
      const order = [];
      (this.interestRateData || []).forEach((row) => {
        const key = row.accountCode || 'Default';
        if (!groups[key]) {
          groups[key] = { accountCode: row.accountCode || '', rows: [] };
          order.push(key);
        }
        groups[key].rows.push(row);
      });
      return order.map((key) => groups[key]);
    },
    // Lowercased accountCodes already present in this metadoc (for the duplicate guard).
    existingAccountCodes() {
      return (this.interestRateData || [])
        .map((r) => (r.accountCode || '').trim().toLowerCase())
        .filter((c) => !!c);
    },
    breadcrumbItems() {
      const titleSubTitleMap = {
        'Product': ['Deposit', 'Loan', 'Insurance', 'Card'],
        'ServiceInfo': ['Service_Info', 'Page_Navigation', 'Branch_Navigation'],
        'Financial_Transaction': ['Transfer', 'TopUp', 'Paybill'],
      };
      let parentTab = this.productCategory || '';
      let isChildOfTab = false;
      for (const [tab, subtitles] of Object.entries(titleSubTitleMap)) {
        if (subtitles.includes(this.productCategory)) {
          parentTab = tab;
          isChildOfTab = true;
          break;
        }
      }
      const subTab = this.fromSubTab || (isChildOfTab ? this.productCategory : '');
      const tabQuery = parentTab ? `?tab=${encodeURIComponent(parentTab)}` : '';
      const subTabQuery = subTab ? `&subTab=${encodeURIComponent(subTab)}` : '';
      return [
        { title: 'Home', disabled: false, href: '/' },
        { title: 'Documents', disabled: false, href: `/metadoc${tabQuery}${subTabQuery}` },
        { title: parentTab || '-', disabled: false, href: `/metadoc${tabQuery}${subTabQuery}` },
        { title: this.itemID || this.vectorDBName || '-', disabled: true },
      ];
    },
    filteredProductTypes() {
      return this.productTypeMap[this.newInterestRate.productCategory] || [];
    },
    filteredProductSubtypes() {
      return this.productSubtypeMap[this.newInterestRate.productType] || [];
    },
  },
  methods: {
    // ── Meta Category options for the Interest Rate form (linkage like Legacy AccountInfo) ──
    loadMetaCategoryOptions() {
      var self = this;
      self.$call.GetMetaAccountCategories(
        function (res) {
          var data = (res && res.status === 0 && res.data) ? res.data : [];
          self.metaCategoryOptions = data.map(function (c) {
            return { title: c.categoryName, value: c.categoryID };
          });
        },
        function () { self.metaCategoryOptions = []; }
      );
    },
    loadMetaSubCategoryOptions(categoryId) {
      var self = this;
      if (!categoryId) { self.metaSubCategoryOptions = []; return; }
      self.$call.GetMetaSubCategoriesByCategory(
        categoryId,
        function (res) {
          var data = (res && res.status === 0 && res.data) ? res.data : [];
          self.metaSubCategoryOptions = data.map(function (s) {
            return { title: s.subCategoryName, value: s.subCategoryID };
          });
        },
        function () { self.metaSubCategoryOptions = []; }
      );
    },
    onNewInterestCategoryChange(categoryId) {
      this.newInterestRate.subCategoryID = null;
      this.loadMetaSubCategoryOptions(categoryId);
    },
    onEditInterestCategoryChange(categoryId) {
      this.editInterestRateItem.subCategoryID = null;
      this.loadMetaSubCategoryOptions(categoryId);
    },

    // ── Meta Category / SubCategory management dialogs ──
    openCategoriesDialog() {
      this.categoriesDialog = true;
      this.loadMetaAccountCategories();
    },
    closeCategoriesDialog() {
      this.categoriesDialog = false;
    },
    loadMetaAccountCategories() {
      var self = this;
      self.loadingMetaCategories = true;
      self.$call.GetMetaAccountCategories(
        function (res) {
          self.metaAccountCategories = (res && res.status === 0 && res.data) ? res.data : [];
          self.loadingMetaCategories = false;
        },
        function () {
          self.metaAccountCategories = [];
          self.loadingMetaCategories = false;
        }
      );
    },
    openCategoryDialog(item) {
      this.categoryEditMode = !!item;
      if (item) {
        this.categoryFormData = Object.assign({}, item);
      } else {
        this.categoryFormData = {
          categoryID: 0,
          categoryCode: '',
          categoryName: '',
          description: '',
          displayOrder: 0,
          isActive: true,
          activeStatus: true,
        };
      }
      this.categoryDialog = true;
    },
    closeCategoryDialog() {
      this.categoryDialog = false;
      if (this.$refs.categoryForm) this.$refs.categoryForm.resetValidation();
    },
    saveCategory() {
      var self = this;
      if (!self.categoryFormValid) return;
      self.savingMetaCategory = true;
      var done = function (res) {
        self.savingMetaCategory = false;
        if (res && res.status === 0) {
          self.$swal.fire({ icon: 'success', title: self.categoryEditMode ? 'Category updated' : 'Category created', showConfirmButton: false, timer: 1500 });
          self.closeCategoryDialog();
          self.loadMetaAccountCategories();
        } else {
          self.$swal.fire({ icon: 'error', title: 'Error', text: (res && res.message) || 'Failed to save category', timer: 2000 });
        }
      };
      var fail = function (err) {
        self.savingMetaCategory = false;
        self.$swal.fire({ icon: 'error', title: 'Error', text: (err && err.response && err.response.data && err.response.data.message) || 'Failed to save category', timer: 2000 });
      };
      if (self.categoryEditMode) {
        self.$call.UpdateMetaAccountCategory(self.categoryFormData.categoryID, self.categoryFormData, done, fail);
      } else {
        self.$call.CreateMetaAccountCategory(self.categoryFormData, done, fail);
      }
    },
    async confirmDeleteCategory(item) {
      var self = this;
      var confirmed = await self.$swal.fire({
        title: 'Are you sure?',
        text: 'Delete category: ' + item.categoryName + '? This also requires removing its subcategories first.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });
      if (confirmed.isConfirmed) {
        self.$call.DeleteMetaAccountCategory(
          item.categoryID,
          function (res) {
            if (res && res.status === 0) {
              self.$swal.fire({ icon: 'success', title: 'Category deleted', showConfirmButton: false, timer: 1500 });
              self.loadMetaAccountCategories();
            } else {
              self.$swal.fire({ icon: 'error', title: 'Error', text: (res && res.message) || 'Failed to delete category', timer: 2500 });
            }
          },
          function (err) {
            self.$swal.fire({ icon: 'error', title: 'Error', text: (err && err.response && err.response.data && err.response.data.message) || 'Failed to delete category', timer: 2500 });
          }
        );
      }
    },
    viewSubCategories(item) {
      this.selectedCategory = item;
      this.categorySubCategories = [];
      this.subCategoriesDialog = true;
      this.loadCategorySubCategories(item.categoryID);
    },
    closeSubCategoriesDialog() {
      this.subCategoriesDialog = false;
      this.selectedCategory = null;
      this.categorySubCategories = [];
    },
    loadCategorySubCategories(categoryId) {
      var self = this;
      self.loadingMetaSubCategories = true;
      self.$call.GetMetaSubCategoriesByCategory(
        categoryId,
        function (res) {
          self.categorySubCategories = (res && res.status === 0 && res.data) ? res.data : [];
          self.loadingMetaSubCategories = false;
        },
        function () {
          self.categorySubCategories = [];
          self.loadingMetaSubCategories = false;
        }
      );
    },
    openSubCategoryDialog(item) {
      this.subCategoryEditMode = !!item;
      if (item) {
        this.subCategoryFormData = Object.assign({}, item);
      } else {
        this.subCategoryFormData = {
          subCategoryID: 0,
          categoryID: this.selectedCategory ? this.selectedCategory.categoryID : 0,
          subCategoryCode: '',
          subCategoryName: '',
          description: '',
          displayOrder: 0,
          isActive: true,
          activeStatus: true,
        };
      }
      this.subCategoryDialog = true;
    },
    closeSubCategoryDialog() {
      this.subCategoryDialog = false;
      if (this.$refs.subCategoryForm) this.$refs.subCategoryForm.resetValidation();
    },
    saveSubCategory() {
      var self = this;
      if (!self.subCategoryFormValid) return;
      self.savingMetaSubCategory = true;
      var done = function (res) {
        self.savingMetaSubCategory = false;
        if (res && res.status === 0) {
          self.$swal.fire({ icon: 'success', title: self.subCategoryEditMode ? 'Subcategory updated' : 'Subcategory created', showConfirmButton: false, timer: 1500 });
          self.closeSubCategoryDialog();
          if (self.selectedCategory) self.loadCategorySubCategories(self.selectedCategory.categoryID);
          self.loadMetaAccountCategories();
        } else {
          self.$swal.fire({ icon: 'error', title: 'Error', text: (res && res.message) || 'Failed to save subcategory', timer: 2000 });
        }
      };
      var fail = function (err) {
        self.savingMetaSubCategory = false;
        self.$swal.fire({ icon: 'error', title: 'Error', text: (err && err.response && err.response.data && err.response.data.message) || 'Failed to save subcategory', timer: 2000 });
      };
      if (self.subCategoryEditMode) {
        self.$call.UpdateMetaAccountSubCategory(self.subCategoryFormData.subCategoryID, self.subCategoryFormData, done, fail);
      } else {
        self.$call.CreateMetaAccountSubCategory(self.subCategoryFormData, done, fail);
      }
    },
    async confirmDeleteSubCategory(item) {
      var self = this;
      var confirmed = await self.$swal.fire({
        title: 'Are you sure?',
        text: 'Delete subcategory: ' + item.subCategoryName + '?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });
      if (confirmed.isConfirmed) {
        self.$call.DeleteMetaAccountSubCategory(
          item.subCategoryID,
          function (res) {
            if (res && res.status === 0) {
              self.$swal.fire({ icon: 'success', title: 'Subcategory deleted', showConfirmButton: false, timer: 1500 });
              if (self.selectedCategory) self.loadCategorySubCategories(self.selectedCategory.categoryID);
              self.loadMetaAccountCategories();
            } else {
              self.$swal.fire({ icon: 'error', title: 'Error', text: (res && res.message) || 'Failed to delete subcategory', timer: 2500 });
            }
          },
          function (err) {
            self.$swal.fire({ icon: 'error', title: 'Error', text: (err && err.response && err.response.data && err.response.data.message) || 'Failed to delete subcategory', timer: 2500 });
          }
        );
      }
    },

    onDocDateChange(obj, key, newVal) {
      obj[key] = newVal || '';
    },
    toggleShowAllText() {
      if(!this.tableData || this.tableData.length < 1) return;
      this.tableData.forEach(row => {
        row.expanded = this.showAllText;
      });
    },
    toggleRowExpand(item) {
      if(!item) return;
      item.expanded = !item.expanded;
    },
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) dd = '0' + dd;
      if (mm < 10) mm = '0' + mm;
      this.today = dd + '/' + mm + '/' + yyyy;
    },
    interestRateControlText(key) {
      const locale = this.$i18n?.locale || localStorage.getItem('locale') || 'en';
      const text = {
        th: {
          allCustomers: 'ลูกค้าทุกคน',
          newCustomersOnly: 'ลูกค้าใหม่เท่านั้น',
          allCustomersTitle: 'ใช้กับลูกค้าทุกคน',
          newCustomersTitle: 'ใช้กับลูกค้าใหม่เท่านั้น',
          allCustomersDescription: 'รายการดอกเบี้ยนี้ใช้ได้กับลูกค้าทุกคน โดยไม่จำกัดว่าเป็นลูกค้าใหม่',
          newCustomersDescription: 'รายการดอกเบี้ยนี้ใช้เฉพาะเงื่อนไขสำหรับลูกค้าใหม่',
        },
        en: {
          allCustomers: 'All customers',
          newCustomersOnly: 'New customers only',
          allCustomersTitle: 'Available for all customers',
          newCustomersTitle: 'Available for new customers only',
          allCustomersDescription: 'This interest item applies to all customers, regardless of new-customer status.',
          newCustomersDescription: 'This interest item applies only to new-customer conditions.',
        },
      };

      return (text[locale] && text[locale][key]) || text.en[key] || key;
    },
    getCustomerScopeText(isNewUser) {
      return isNewUser ? this.interestRateControlText('newCustomersOnly') : this.interestRateControlText('allCustomers');
    },
    getCustomerScopeToggleTitle(isNewUser) {
      return isNewUser ? this.interestRateControlText('newCustomersTitle') : this.interestRateControlText('allCustomersTitle');
    },
    getCustomerScopeDescription(isNewUser) {
      return isNewUser ? this.interestRateControlText('newCustomersDescription') : this.interestRateControlText('allCustomersDescription');
    },
    onResizeWindow() {
      this.windowWidth = window.innerWidth;
    },
    wrapHeaderTitle(title) {
      
      const breakAll = this.windowWidth < 1400;
      if (breakAll) {
        return title.replace(/_/g, '_\n').replace(/ /g, '\n');
      } else {
        return title.replace(/_/, '_\n').replace(/ /g, '\n');
      }
    },
    addDocument() {
      this.newDoc = {
        fileName: '',
        file: null,
        effectiveStart: '',
        effectiveEnd: '',
        json: '',
        webLink: '',
        universalLink: '',
        channels: [],
        channelOtherEnabled: false,
        channelOther: '',
        note: '',
        status: 'Active',
        vectorType: null,
        modelType: null,
        customCharacter: null,
        maxLimitedCharacter: null,
        chunkOverlapChars: 0,
        refineWithLLM: false,
      };
      this.filterDDType = [];
      this.filterdatavectortype = [];
      this.dialogAddDoc = true;
      this.$nextTick(() => this.$refs.addDocForm?.resetValidation());
    },
    // Filter VectorType options based on file extension (mirrors UploadVector.vue)
    filterVectorTypeByFile(fileName) {
      const ext = (fileName || '').toLowerCase().split('.').pop();
      if (ext === 'csv' || ext === 'xlsx' || ext === 'xls') {
        this.filterdatavectortype = this.datavectortype.filter(e => e.value === 1 || e.value === 3 || e.value === 4);
      } else if (ext === 'pdf' || ext === 'txt' || ext === 'docx' || ext === 'doc' || ext === 'pptx' || ext === 'ppt') {
        this.filterdatavectortype = this.datavectortype.filter(e => e.value === 1 || e.value === 2 || e.value === 4);
      } else {
        this.filterdatavectortype = this.datavectortype.filter(e => e.value === 4);
      }
      this.newDoc.vectorType = null;
      this.newDoc.modelType = null;
      this.newDoc.refineWithLLM = false;
      this.filterDDType = [];
    },
    onVectorTypeChange(val) {
      // Mirror legacy mapping (UploadVector.vue): General→1,2,3 / Article→4 / PriceList→5,6 / MD→Auto(9)
      this.newDoc.modelType = null;
      this.newDoc.customCharacter = null;
      this.newDoc.maxLimitedCharacter = null;
      this.newDoc.chunkOverlapChars = 0;
      this.newDoc.refineWithLLM = false;

      if (val === 1) {
        this.filterDDType = this.DDType.filter(e => e.value === 1 || e.value === 2 || e.value === 3);
      } else if (val === 2) {
        this.filterDDType = this.DDType.filter(e => e.value === 4);
      } else if (val === 3) {
        this.filterDDType = this.DDType.filter(e => e.value === 5 || e.value === 6);
      } else if (val === 4) {
        this.filterDDType = this.DDType.filter(e => e.value === 9 || e.value === 10 || e.value === 11);
      } else {
        this.filterDDType = [];
      }
      this.$nextTick(() => this.$refs.addDocForm?.validate());
    },
    onModelTypeChange(newVal) {
      if (newVal !== 3 && newVal !== 10) this.newDoc.customCharacter = null;
      if (newVal !== 2) this.newDoc.maxLimitedCharacter = null;
      if (newVal !== 9 && newVal !== 11) this.newDoc.chunkOverlapChars = 0;
      if (!this.supportsRefine) this.newDoc.refineWithLLM = false;
      this.$nextTick(() => this.$refs.addDocForm?.validate());
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.newDoc.file = file;
      this.newDoc.fileName = file.name;
      this.filterVectorTypeByFile(file.name);
      event.target.value = '';
      this.$nextTick(() => this.$refs.addDocForm?.validate());
    },
    async saveNewDocument() {
      const { valid } = await this.$refs.addDocForm.validate();
      if (!valid) return;

      let self = this;
      self.loadingTable = true;
      self.dialogAddDoc = false;

      // Build channel availability JSON
      let channels = [...(self.newDoc.channels || [])];
      if (self.newDoc.channelOtherEnabled && self.newDoc.channelOther) {
        channels.push(self.newDoc.channelOther);
      }

      // Single FormData call with both metadata + file
      const formData = new FormData();
      formData.append('vectorDBName', self.vectorDBName || '');
      formData.append('fileName', self.newDoc.fileName || '');
      if (self.newDoc.effectiveStart) formData.append('effectiveStartDate', self.newDoc.effectiveStart);
      if (self.newDoc.effectiveEnd) formData.append('effectiveEndDate', self.newDoc.effectiveEnd);
      if (self.newDoc.universalLink) formData.append('universalLink', self.newDoc.universalLink);
      if (self.newDoc.webLink) formData.append('webLink', self.newDoc.webLink);
      if (self.newDoc.json) formData.append('jsonData', self.newDoc.json);
      if (channels.length > 0) formData.append('channelAvailability', JSON.stringify(channels));
      if (self.newDoc.note) formData.append('note', self.newDoc.note);
      formData.append('activeStatus', self.newDoc.status === 'Active');
      formData.append('title', self.productCategory || '');
      formData.append('subTitle', self.subTitle || '');

      // Chunking method selection
      if (self.newDoc.modelType != null) formData.append('modelType', self.newDoc.modelType);
      if ((self.newDoc.modelType === 3 || self.newDoc.modelType === 10) && self.newDoc.customCharacter != null) {
        formData.append('customCharacter', self.newDoc.customCharacter);
      }
      if (self.newDoc.modelType === 2 && self.newDoc.maxLimitedCharacter != null) {
        formData.append('maxLimitedCharacter', self.newDoc.maxLimitedCharacter);
      }
      formData.append('chunkOverlapChars', self.newDoc.chunkOverlapChars || 0);
      if (self.supportsRefine && self.newDoc.refineWithLLM) {
        formData.append('refineWithLLM', true);
        if (self.newDoc.vectorType != null) formData.append('vectorType', self.newDoc.vectorType);
      }

      // Attach file for chunking
      if (self.newDoc.file) formData.append('entity', self.newDoc.file);
      const hasFile = !!self.newDoc.file;

      self.$call.SaveDocumentVersionForm(
        formData,
        (dataResponse) => {
          self.loadingTable = false;
          if (dataResponse.status == 0) {
            let msg = hasFile ? 'Document version saved & processing started' : 'Document version saved';
            self.$swal.fire({ icon: 'success', title: msg, showConfirmButton: false, timer: 1500 });
            self.loadDocumentVersions();
          } else {
            self.$swal.fire({ icon: 'error', title: 'Error', text: dataResponse.message || 'Failed to save', timer: 2000 });
            // the version row may already exist (e.g. LLM refine could not read the file → Failed)
            self.loadDocumentVersions();
          }
        },
        (errorResource) => {
          self.loadingTable = false;
          console.error('SaveDocumentVersion error:', errorResource);
        }
      );
    },
    viewDocument(item) {
      let self = this;
      let channels = [];
      try {
        const raw = item.channelAvailability || '';
        channels = raw === '-' ? [] : raw.split(', ').filter(Boolean);
      } catch { channels = []; }

      this.viewDoc = {
        itemName: this.vectorDBName || item.fileName || '-',
        version: item.versionLabel || item.version || '-',
        headId: item.id,
        chunks: [],
        json: item.json === '-' ? '' : (item.json || ''),
        webLink: item.webLink === '-' ? '' : (item.webLink || ''),
        universalLink: item.universalLink === '-' ? '' : (item.universalLink || ''),
        channels: channels.filter(c => ['Mobile', 'CRM'].includes(c)),
        channelOtherEnabled: channels.some(c => !['Mobile', 'CRM'].includes(c)),
        channelOther: channels.find(c => !['Mobile', 'CRM'].includes(c)) || '',
        note: item.note === '-' ? '' : (item.note || ''),
        status: item.status || 'Active',
      };
      this.dialogViewDoc = true;

      // Load actual chunks from vector DB (pass headId for version-specific filtering)
      self.$call.GetDocumentChunks(
        { vectorDBName: self.vectorDBName, headId: item.id },
        (dataResponse) => {
          if (dataResponse.status == 0 && dataResponse.data) {
            self.viewDoc.chunks = dataResponse.data || [];
          }
        },
        (err) => { console.error('Load chunks error:', err); }
      );
    },
    openEditChunk(chunk) {
      if (!this.canEdit || !chunk) return;
      this.editChunk = {
        id: chunk.id,
        text: chunk.text || '',
        originalText: chunk.text || '',
      };
      this.dialogEditChunk = true;
    },
    closeEditChunk() {
      if (this.savingChunk) return;
      this.dialogEditChunk = false;
      this.editChunk = { id: null, text: '', originalText: '' };
    },
    saveEditChunk() {
      let self = this;
      const content = (self.editChunk.text || '').trim();
      if (!content) {
        self.$swal.fire({ icon: 'warning', title: 'Please enter chunk content', timer: 1800, showConfirmButton: false });
        return;
      }

      if (!self.editChunk.id || !self.viewDoc.headId) {
        self.$swal.fire({ icon: 'error', title: 'Unable to edit this chunk', timer: 1800, showConfirmButton: false });
        return;
      }

      self.savingChunk = true;
      self.$call.SaveEditDocumentChunk(
        { id: self.editChunk.id, content: self.editChunk.text, headId: self.viewDoc.headId },
        (dataResponse) => {
          self.savingChunk = false;
          if (dataResponse.status == 0) {
            const chunk = self.viewDoc.chunks.find(c => c.id === self.editChunk.id);
            if (chunk) chunk.text = self.editChunk.text;
            self.closeEditChunk();
            self.loadDocumentVersions();
            self.$swal.fire({ icon: 'success', title: 'Chunk saved & re-vectorization started', showConfirmButton: false, timer: 1800 });
          } else {
            self.$swal.fire({ icon: 'error', title: 'Error', text: dataResponse.message || 'Failed to save chunk', timer: 2200 });
          }
        },
        (err) => {
          self.savingChunk = false;
          console.error('SaveEditDocumentChunk error:', err);
          self.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save chunk', timer: 2200 });
        }
      );
    },
    exportDocument(item) {
      let self = this;
      self.$call.GetDocumentChunks(
        { vectorDBName: self.vectorDBName, headId: item.id },
        async (dataResponse) => {
          const chunks = (dataResponse.status == 0 && dataResponse.data) ? dataResponse.data : [];
          const workbook = new ExcelJS.Workbook();
          const sheet = workbook.addWorksheet('Document');

          sheet.columns = [
            { header: 'No', key: 'no', width: 8 },
            { header: 'Version', key: 'version', width: 12 },
            { header: 'Status', key: 'status', width: 12 },
            { header: 'File_name', key: 'fileName', width: 25 },
            { header: 'Effective_start_date', key: 'effectiveStartDate', width: 22 },
            { header: 'Effective_end_date', key: 'effectiveEndDate', width: 22 },
            { header: 'Universal_link', key: 'universalLink', width: 25 },
            { header: 'Web_link', key: 'webLink', width: 25 },
            { header: 'JSON', key: 'json', width: 15 },
            { header: 'Channel_availability', key: 'channelAvailability', width: 22 },
            { header: 'Added_by', key: 'addedBy', width: 15 },
            { header: 'Added_date', key: 'addedDate', width: 20 },
            { header: 'Last_Update_By', key: 'updateBy', width: 18 },
            { header: 'Last_Update_Date', key: 'updatedDate', width: 20 },
            { header: 'Note', key: 'note', width: 20 },
            { header: 'Chunk', key: 'chunk', width: 60 },
          ];

          const headerRow = sheet.getRow(1);
          headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          headerRow.alignment = { vertical: 'middle' };
          headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B2869' } };
          });

          if (chunks.length === 0) {
            sheet.addRow({
              no: 1,
              version: item.version || '-',
              status: item.status || '-',
              fileName: item.fileName || '-',
              effectiveStartDate: item.effectiveStartDate || '-',
              effectiveEndDate: item.effectiveEndDate || '-',
              universalLink: item.universalLink || '-',
              webLink: item.webLink || '-',
              json: item.json || '-',
              channelAvailability: item.channelAvailability || '-',
              addedBy: item.addedBy || '-',
              addedDate: item.addedDate || '-',
              updateBy: item.updateBy || '-',
              updatedDate: item.updatedDate || '-',
              note: item.note || '-',
              chunk: '',
            });
          } else {
            chunks.forEach((chunk, i) => {
              sheet.addRow({
                no: i + 1,
                version: item.version || '-',
                status: item.status || '-',
                fileName: item.fileName || '-',
                effectiveStartDate: item.effectiveStartDate || '-',
                effectiveEndDate: item.effectiveEndDate || '-',
                universalLink: item.universalLink || '-',
                webLink: item.webLink || '-',
                json: item.json || '-',
                channelAvailability: item.channelAvailability || '-',
                addedBy: item.addedBy || '-',
                addedDate: item.addedDate || '-',
                updateBy: item.updateBy || '-',
                updatedDate: item.updatedDate || '-',
                note: item.note || '-',
                chunk: chunk.text || '',
              });
            });
          }

          const buffer = await workbook.xlsx.writeBuffer();
          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${self.vectorDBName || 'document'}_${item.version || 'export'}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        },
        (err) => { console.error('Export chunks error:', err); }
      );
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
    },
    editDocument(item) {
      this.editDoc = {
        id: item.id,
        itemName: this.vectorDBName || item.fileName || '-',
        version: item.version || '-',
        effectiveStart: item.effectiveStartDateRaw ? this.toDatetimeLocal(item.effectiveStartDateRaw) : '',
        effectiveEnd: item.effectiveEndDateRaw ? this.toDatetimeLocal(item.effectiveEndDateRaw) : '',
        note: item.note || '',
        status: item.activeStatus ? 'Active' : 'Inactive',
      };
      this.dialogEditDoc = true;
    },
    toDatetimeLocal(dateStr) {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const pad = (n) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return ''; }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      try {
        const d = new Date(dateStr);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return '-'; }
    },
    formatUtcDate(dateStr) {
      if (!dateStr) return '-';
      try {
        const iso = typeof dateStr === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr)
          ? `${dateStr}Z`
          : dateStr;
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleString('en-GB', {
          timeZone: 'Asia/Bangkok',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).replace(',', '');
      } catch { return '-'; }
    },
    async saveEditDocument() {
      const { valid } = await this.$refs.editDocForm.validate();
      if (!valid) return;

      let self = this;
      self.loadingTable = true;
      self.dialogEditDoc = false;

      const validDate = (v) => v && !v.includes('NaN') ? v : null;
      var request = {
        id: self.editDoc.id,
        effectiveStartDate: validDate(self.editDoc.effectiveStart),
        effectiveEndDate: validDate(self.editDoc.effectiveEnd),
        note: self.editDoc.note || null,
        activeStatus: self.editDoc.status === 'Active',
      };

      self.$call.UpdateDocumentVersion(
        request,
        (dataResponse) => {
          self.loadingTable = false;
          if (dataResponse.status == 0) {
            self.$swal.fire({ icon: 'success', title: 'Document updated', showConfirmButton: false, timer: 1500 });
            self.loadDocumentVersions();
          } else {
            self.$swal.fire({ icon: 'error', title: 'Error', text: dataResponse.message || 'Failed to update', timer: 2000 });
          }
        },
        (errorResource) => {
          self.loadingTable = false;
          console.error('UpdateDocumentVersion error:', errorResource);
        }
      );
    },
    addInterestRate(preset) {
      // Lock the accountCode field only when adding a version to an existing account.
      this.lockAccountCode = !!(preset && preset.accountCode);
      this.newInterestRate = {
        productCategory: this.productCategory || '',
        productType: this.subTitle || '',
        productSubtype: this.productSubtype || '',
        calculationMethod: '',
        paymentFrequency: '',
        tiers: [],
        variants: [],
        effectiveStart: '',
        effectiveEnd: '',
        note: '',
        status: 'Active',
        accountCode: (preset && preset.accountCode) || '',
        accountName: (preset && preset.accountName) || '',
        categoryID: null,
        subCategoryID: null,
        interestRateName: '',
        interestRateType: '',
        mrrRate: 0,
        loanCategoryID: null,
        isNewUser: false,
      };
      // Load Meta category options for the Deposit category/subcategory selects.
      this.loadMetaCategoryOptions();
      this.metaSubCategoryOptions = [];
      this.onProductCategoryChange(this.newInterestRate);
      // Re-assign productType/productSubtype after onProductCategoryChange (which resets them)
      if (this.subTitle) {
        this.newInterestRate.productType = this.subTitle;
        this.onProductTypeChange(this.newInterestRate);
      }
      if (this.productSubtype) {
        this.newInterestRate.productSubtype = this.productSubtype;
      }
      // "Add Version" locks the group's accountCode; accountName stays empty so each version is named individually.
      if (preset && preset.accountCode) this.newInterestRate.accountCode = preset.accountCode;
      // Load occupations for Personal Loan
      if (this.productCategory === 'Loan') {
        this.loadOccupationsData();
      }
      this.dialogAddInterestRate = true;
    },
    async saveNewInterestRate() {
      const { valid } = await this.$refs.addInterestForm.validate();
      if (!valid) return;

      var self = this;
      const payload = {
        vectorDBName: self.vectorDBName,
        productCategory: self.newInterestRate.productCategory,
        productType: self.newInterestRate.productType,
        productSubtype: self.newInterestRate.productSubtype,
        calculationMethod: self.newInterestRate.calculationMethod,
        paymentFrequency: self.newInterestRate.paymentFrequency,
        // Users enter rates as percent (e.g. 3 for 3%); DB stores decimal fraction (0.03).
        // The server DTO types this as `string` (MetaDocInterestRateTier.InterestRate), so coerce.
        tiers: (self.newInterestRate.tiers || []).map(function(t) {
          return Object.assign({}, t, { interestRate: String(self.pctToDecimal(t.interestRate)) });
        }),
        effectiveStartDate: self.newInterestRate.effectiveStart || null,
        effectiveEndDate: self.newInterestRate.effectiveEnd || null,
        note: self.newInterestRate.note,
        activeStatus: self.newInterestRate.status === 'Active',
        // SQL sync fields
        accountCode: self.newInterestRate.accountCode || null,
        accountName: self.newInterestRate.accountName || null,
        categoryID: self.newInterestRate.categoryID || null,
        subCategoryID: self.newInterestRate.subCategoryID || null,
        interestRateName: self.newInterestRate.interestRateName || null,
        interestRateType: self.newInterestRate.interestRateType || null,
        mrrRate: self.newInterestRate.mrrRate ? self.pctToDecimal(self.newInterestRate.mrrRate) : null,
        loanCategoryID: self.newInterestRate.loanCategoryID || null,
      };
      if (self.newInterestRate.productCategory === 'Deposit') {
        payload.isNewUser = self.newInterestRate.isNewUser === true;
      }
      // Add variants for Loan products
      if (self.newInterestRate.productCategory === 'Loan' &&
          (self.newInterestRate.productType === 'Home_Loan' || self.newInterestRate.productType === 'Personal_Loan')) {
        payload.variants = self.buildAllVariantsPayload();
      }
      self.$call.AddMetaDocInterestRate(payload, function(res) {
        self.dialogAddInterestRate = false;
        self.loadInterestRates();
      });
    },
    addTierToList(tiersList) {
      tiersList.push({
        tierName: '',
        lowerAmount: '',
        upperAmount: '',
        interestRate: '',
        minDay: '',
        maxDay: '',
      });
    },
    removeTierFromList(tiersList, index) {
      tiersList.splice(index, 1);
    },
    onProductCategoryChange(model) {
      model.productType = '';
      model.productSubtype = '';
      model.variants = [];
      model.tiers = [];
      model.interestRateType = '';
      model.calculationMethod = '';
      model.paymentFrequency = '';
      model.mrrRate = 0;
    },
    onProductTypeChange(model) {
      model.productSubtype = '';
      // Auto-set interestRateType from productType for Loan
      if (model.productCategory === 'Loan') {
        model.interestRateType = model.productType;
        model.interestRateName = (this.parentDocNameEN || this.parentDocNameTH) ? 'EN:' + this.parentDocNameEN + ' TH:' + this.parentDocNameTH : '';
        var fixedRateCat = this.categoriesData.find(function(c) { return c.categoryName === 'Fixed Rate'; });
        model.loanCategoryID = fixedRateCat ? fixedRateCat.categoryID : 1;
        if (model.productType === 'Home_Loan') {
          model.mrrRate = 0;
          model.variants = [
            {
              variantID: 0,
              variantName: 'ตัวเลือกเริ่มต้น',
              variantDescription: '',
              sortOrder: 1,
              isActive: true,
              installmentType: 'Normal',
              details: createDefaultHomeLoanDetails(),
            }
          ];
          model.tiers = [];
        } else if (model.productType === 'Personal_Loan') {
          const defaultMonths = createDefaultPersonalLoanMonths();
          model.variants = [
            {
              variantID: 0,
              variantName: 'ตัวเลือกเริ่มต้น',
              variantDescription: '',
              sortOrder: 1,
              isActive: true,
              personalLoanMonths: defaultMonths,
              personalLoanData: createDefaultPersonalLoanData(defaultMonths.length),
            }
          ];
          model.tiers = [];
        } else {
          // Car Loan or other — reset
          model.variants = [];
          model.tiers = [];
        }
      } else {
        // Deposit — use tiers. Leave accountName empty by default (user enters it manually).
        model.variants = [];
        model.tiers = [];
        model.interestRateType = '';
      }
    },

    getOccupationName(occupationId) {
      if (!occupationId) return '-';
      var found = this.occupationsData.find(function(o) { return o.occupationID === occupationId; });
      return found ? found.occupationNameTH : 'ID: ' + occupationId;
    },
    formatSalaryRange(min, max) {
      if (min == null && max == null) return '-';
      var minStr = min != null ? Number(min).toLocaleString() : '0';
      var maxStr = max != null ? Number(max).toLocaleString() : '+';
      return minStr + ' - ' + maxStr;
    },

    // ── Variant Management Methods ──
    addVariant() {
      const model = this.newInterestRate;
      const newVariant = {
        variantID: 0,
        variantName: `ตัวเลือก ${model.variants.length + 1}`,
        variantDescription: '',
        sortOrder: model.variants.length + 1,
        isActive: true,
        installmentType: 'Normal',
      };
      if (model.productType === 'Home_Loan') {
        newVariant.details = createDefaultHomeLoanDetails();
      } else if (model.productType === 'Personal_Loan') {
        const defaultMonths = createDefaultPersonalLoanMonths();
        newVariant.personalLoanMonths = defaultMonths;
        newVariant.personalLoanData = createDefaultPersonalLoanData(defaultMonths.length);
      }
      model.variants.push(newVariant);
      if (model.productType === 'Home_Loan') {
        this.calculateAllTotalRatesForAllVariants();
      }
    },
    removeVariant(variantIndex) {
      const model = this.newInterestRate;
      if (model.variants.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'ต้องมีอย่างน้อย 1 ตัวเลือก',
        });
        return;
      }
      this.$swal.fire({
        title: 'ยืนยันการลบ?',
        text: `คุณต้องการลบ "${model.variants[variantIndex].variantName}" หรือไม่?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'ลบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          model.variants.splice(variantIndex, 1);
          model.variants.forEach((v, idx) => {
            v.sortOrder = idx + 1;
          });
        }
      });
    },
    calculateTotalRateForVariant(variant, item) {
      const mrrRate = parseFloat(this.newInterestRate.mrrRate) || 0;
      const difference = parseFloat(item.inputInterestDifference) || 0;
      let totalRate = 0;
      if (item.interestType === 'Fixed') {
        totalRate = difference;
      } else if (item.interestType === 'MRR') {
        totalRate = mrrRate - difference;
      }
      item.totalInterestRate = totalRate.toFixed(3) + '%';
    },
    calculateAllTotalRatesForAllVariants() {
      const model = this.newInterestRate;
      if (!model.variants || !Array.isArray(model.variants)) return;
      model.variants.forEach(variant => {
        if (variant.details && Array.isArray(variant.details)) {
          variant.details.forEach(item => {
            this.calculateTotalRateForVariant(variant, item);
          });
        }
      });
    },
    getMonthRangeDisplay(item) {
      if (!item.monthRangeEnd) {
        return `${item.monthRangeStart} And Above`;
      }
      return `${item.monthRangeStart}-${item.monthRangeEnd}`;
    },
    transformPersonalLoanRatesToUi(personalLoanRates) {
      if (!Array.isArray(personalLoanRates) || !personalLoanRates.length) return null;
      var monthValues = Array.from(
        new Set(
          personalLoanRates
            .map(function(r) { return Number(r.tenorMonths ?? r.tenorMonth); })
            .filter(function(v) { return !Number.isNaN(v) && v > 0; })
        )
      ).sort(function(a, b) { return a - b; });
      if (!monthValues.length) return null;
      var monthIndexMap = new Map(monthValues.map(function(v, i) { return [v, i]; }));
      var profMap = new Map();
      personalLoanRates.forEach(function(rate) {
        var occId = Number(rate.occupationID ?? rate.occupationId);
        var tenor = Number(rate.tenorMonths ?? rate.tenorMonth);
        if (Number.isNaN(occId) || Number.isNaN(tenor) || !monthIndexMap.has(tenor)) return;
        if (!profMap.has(occId)) profMap.set(occId, new Map());
        var incMin = rate.incomeMin != null ? Number(rate.incomeMin) : null;
        var incMax = rate.incomeMax != null ? Number(rate.incomeMax) : null;
        var rangeKey = (incMin ?? 'null') + '-' + (incMax ?? 'null');
        var ranges = profMap.get(occId);
        if (!ranges.has(rangeKey)) {
          ranges.set(rangeKey, { rangeMin: incMin, rangeMax: incMax, interestRates: Array(monthValues.length).fill(0) });
        }
        var entry = ranges.get(rangeKey);
        var val = Number(rate.interestRate ?? rate.rate);
        // DB stores decimal fraction; UI input/display is percent.
        entry.interestRates[monthIndexMap.get(tenor)] = Number.isNaN(val)
          ? 0
          : Number((val * 100).toFixed(2));
      });
      var personalLoanData = Array.from(profMap.entries())
        .map(function(e) {
          return { occupationId: e[0], salaryRanges: Array.from(e[1].values()).sort(function(a, b) { return (a.rangeMin ?? 0) - (b.rangeMin ?? 0); }) };
        })
        .sort(function(a, b) { return a.occupationId - b.occupationId; });
      return {
        personalLoanMonths: monthValues.map(function(v) { return { value: String(v) }; }),
        personalLoanData: personalLoanData
      };
    },

    // Personal Loan Methods
    addPersonalLoanMonthForVariant(variantIndex) {
      const variant = this.newInterestRate.variants[variantIndex];
      variant.personalLoanMonths.push({ value: '' });
      variant.personalLoanData.forEach(profession => {
        profession.salaryRanges.forEach(range => {
          range.interestRates.push(null);
        });
      });
    },
    removePersonalLoanMonthForVariant(variantIndex, monthIndex) {
      const variant = this.newInterestRate.variants[variantIndex];
      if (variant.personalLoanMonths.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'Must have at least one month column',
        });
        return;
      }
      variant.personalLoanMonths.splice(monthIndex, 1);
      variant.personalLoanData.forEach(profession => {
        profession.salaryRanges.forEach(range => {
          range.interestRates.splice(monthIndex, 1);
        });
      });
    },
    addProfessionForVariant(variantIndex) {
      const variant = this.newInterestRate.variants[variantIndex];
      variant.personalLoanData.push({
        occupationId: null,
        salaryRanges: [
          {
            rangeMin: null,
            rangeMax: null,
            interestRates: new Array(variant.personalLoanMonths.length).fill(null),
          },
        ],
      });
    },
    removeProfessionForVariant(variantIndex, profIndex) {
      const variant = this.newInterestRate.variants[variantIndex];
      if (variant.personalLoanData.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'Must have at least one profession',
        });
        return;
      }
      variant.personalLoanData.splice(profIndex, 1);
    },
    addSalaryRangeForVariant(variantIndex, profIndex) {
      const variant = this.newInterestRate.variants[variantIndex];
      variant.personalLoanData[profIndex].salaryRanges.push({
        rangeMin: null,
        rangeMax: null,
        interestRates: new Array(variant.personalLoanMonths.length).fill(null),
      });
    },
    removeSalaryRangeForVariant(variantIndex, profIndex, rangeIndex) {
      const variant = this.newInterestRate.variants[variantIndex];
      const profession = variant.personalLoanData[profIndex];
      if (profession.salaryRanges.length <= 1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'Must have at least one salary range',
        });
        return;
      }
      profession.salaryRanges.splice(rangeIndex, 1);
    },
    validateNumericKeypress(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },
    // UI edits rates in percent units (e.g. "3" meaning 3%); DB stores decimal fraction (0.03).
    // Use these at every boundary so users still see "3%" while the optimizer sees 0.03.
    pctToDecimal(v) {
      const n = parseFloat(v);
      return Number.isNaN(n) ? 0 : n / 100;
    },
    decimalToPct(v) {
      const n = parseFloat(v);
      return Number.isNaN(n) ? 0 : n * 100;
    },
    formatPercentDisplay(v) {
      const n = typeof v === 'string' ? parseFloat(v.replace('%', '')) : Number(v);
      if (Number.isNaN(n)) return '0';
      return n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    },
    formatAmountDisplay(v) {
      if (v === null || v === undefined || v === '') return '-';
      const n = typeof v === 'string' ? parseFloat(v.replace(/,/g, '')) : Number(v);
      if (Number.isNaN(n)) return '-';
      // Thousand separators; keep decimals only when present
      return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
    },
    filterNumericInput(event, month) {
      const value = event.target.value;
      const filtered = value.replace(/[^0-9]/g, '');
      month.value = filtered;
      this.$nextTick(() => {
        event.target.value = filtered;
      });
    },
    buildPersonalLoanRatesPayloadForVariant(variant) {
      const payload = [];
      (variant.personalLoanData || []).forEach((profession) => {
        let occupationId;
        if (typeof profession.occupationId === 'object' && profession.occupationId !== null) {
          occupationId = Number(profession.occupationId.occupationID);
        } else {
          occupationId = Number(profession.occupationId);
        }
        if (Number.isNaN(occupationId) || occupationId <= 0) return;

        (profession.salaryRanges || []).forEach((range) => {
          const hasMin = range.rangeMin !== undefined && range.rangeMin !== null && range.rangeMin !== '';
          if (!hasMin) return;
          const incomeMin = Number(range.rangeMin);
          if (Number.isNaN(incomeMin)) return;
          const hasMax = range.rangeMax !== undefined && range.rangeMax !== null && range.rangeMax !== '';
          const incomeMax = hasMax ? Number(range.rangeMax) : null;
          if (hasMax && Number.isNaN(incomeMax)) return;

          const interestRates = Array.isArray(range.interestRates) ? range.interestRates : [];
          (variant.personalLoanMonths || []).forEach((month, monthIndex) => {
            const monthValue = Number(month.value);
            if (Number.isNaN(monthValue) || monthValue <= 0) return;
            const rateValueRaw = interestRates[monthIndex];
            if (rateValueRaw === undefined || rateValueRaw === null || rateValueRaw === '') return;
            const rateValue = Number(rateValueRaw);
            if (Number.isNaN(rateValue)) return;
            payload.push({
              occupationID: occupationId,
              incomeMin,
              incomeMax,
              tenorMonths: monthValue,
              // rateValue is percent units (e.g. 5.5 for 5.5%); DB stores decimal fraction.
              interestRate: this.pctToDecimal(rateValue),
              isActive: true,
              interestRateID: 0,
            });
          });
        });
      });
      return payload;
    },
    buildAllVariantsPayload() {
      const model = this.newInterestRate;
      if (!model.variants || !Array.isArray(model.variants)) return [];
      return model.variants.map(variant => ({
        variantID: variant.variantID || 0,
        variantName: variant.variantName,
        variantDescription: variant.variantDescription || '',
        sortOrder: variant.sortOrder,
        isActive: variant.isActive !== false,
        installmentType: variant.installmentType || 'Normal',
        details: model.productType === 'Home_Loan' ?
          (variant.details || []).map(detail => ({
            monthRange: `${detail.monthRangeStart} - ${detail.monthRangeEnd || 'And Above'}`,
            monthRangeStart: detail.monthRangeStart,
            monthRangeEnd: detail.monthRangeEnd,
            interestType: detail.interestType,
            inputInterestDifference: this.pctToDecimal(detail.inputInterestDifference)
          })) : undefined,
        personalLoanRates: model.productType === 'Personal_Loan' ?
          this.buildPersonalLoanRatesPayloadForVariant(variant) : undefined
      }));
    },
    async loadOccupationsData() {
      var self = this;
      self.$call.GetInterestRateCategories(function(res) {
        self.occupationsData = res.data.occupations || [];
      });
    },

    viewInterestRate(item) {
      var self = this;
      var viewVariants = [];
      if (item.productCategory === 'Loan' && item.variants && item.variants.length) {
        viewVariants = item.variants.map(function(v) {
          var mapped = {
            variantName: v.variantName || '',
            variantDescription: v.variantDescription || '',
            installmentType: v.installmentType || 'Normal',
            sortOrder: v.sortOrder || 0,
            // DB rates are decimal fraction; template appends '%', so scale up for display.
            details: (v.details || []).map(function(d) {
              return Object.assign({}, d, {
                inputInterestDifference: self.decimalToPct(d.inputInterestDifference),
                totalInterestRate: typeof d.totalInterestRate === 'number'
                  ? self.decimalToPct(d.totalInterestRate)
                  : d.totalInterestRate,
              });
            }),
            personalLoanMonths: [],
            personalLoanData: [],
          };
          if (item.productType === 'Personal_Loan' && v.personalLoanRates && v.personalLoanRates.length) {
            var transformed = self.transformPersonalLoanRatesToUi(v.personalLoanRates);
            if (transformed) {
              mapped.personalLoanMonths = transformed.personalLoanMonths;
              mapped.personalLoanData = transformed.personalLoanData;
            }
          }
          return mapped;
        });
      }
      this.viewInterestRateItem = {
        version: item.version || '-',
        accountCode: item.accountCode || '-',
        accountName: item.accountName || '-',
        productCategory: item.productCategory || '-',
        productType: item.productType || '-',
        productSubtype: item.productSubtype || '-',
        calculationMethod: item.calculationMethod || '-',
        paymentFrequency: item.paymentFrequency || '-',
        // Scale decimal-fraction rates back to percent for display.
        tiers: (item.tiers || []).map(function(t) {
          return Object.assign({}, t, { interestRate: self.decimalToPct(t.interestRate) });
        }),
        mrrRate: self.decimalToPct(item.mrrRate),
        variants: viewVariants,
        effectiveStartDate: item.effectiveStartDate || '-',
        effectiveEndDate: item.effectiveEndDate || '-',
        note: item.note || '',
        status: item.status || 'Active',
        isNewUser: item.isNewUser === true,
        categoryID: item.categoryID || null,
        subCategoryID: item.subCategoryID || null,
        categoryName: '-',
        subCategoryName: '-',
      };
      this.dialogViewInterestRate = true;
      // Resolve category/subcategory names for display.
      this.resolveViewCategoryNames(item.categoryID, item.subCategoryID);
    },
    resolveViewCategoryNames(categoryId, subCategoryId) {
      var self = this;
      if (!categoryId) return;
      self.$call.GetMetaAccountCategories(function (res) {
        var data = (res && res.status === 0 && res.data) ? res.data : [];
        var cat = data.find(function (c) { return c.categoryID === categoryId; });
        if (cat) self.viewInterestRateItem.categoryName = cat.categoryName;
      });
      if (subCategoryId) {
        self.$call.GetMetaSubCategoriesByCategory(categoryId, function (res) {
          var data = (res && res.status === 0 && res.data) ? res.data : [];
          var sub = data.find(function (s) { return s.subCategoryID === subCategoryId; });
          if (sub) self.viewInterestRateItem.subCategoryName = sub.subCategoryName;
        });
      }
    },
    editInterestRate(item) {
      this.editInterestRateItem = {
        id: item.id,
        version: item.version || '-',
        accountCode: item.accountCode || '',
        accountName: item.accountName || '',
        productCategory: item.productCategory || '',
        effectiveStart: item.effectiveStartDateRaw ? this.toDatetimeLocal(item.effectiveStartDateRaw) : '',
        effectiveEnd: item.effectiveEndDateRaw ? this.toDatetimeLocal(item.effectiveEndDateRaw) : '',
        note: item.note || '',
        status: item.status || 'Active',
        isNewUser: item.isNewUser === true,
        categoryID: item.categoryID || null,
        subCategoryID: item.subCategoryID || null,
      };
      // Load Meta category options and the subcategories for the current category.
      this.loadMetaCategoryOptions();
      if (this.editInterestRateItem.categoryID) {
        this.loadMetaSubCategoryOptions(this.editInterestRateItem.categoryID);
      } else {
        this.metaSubCategoryOptions = [];
      }
      this.dialogEditInterestRate = true;
    },
    saveEditInterestRate() {
      var self = this;
      const validDate = (v) => v && !v.includes('NaN') ? v : null;
      const request = {
        vectorDBName: self.vectorDBName,
        id: self.editInterestRateItem.id,
        effectiveStartDate: validDate(self.editInterestRateItem.effectiveStart),
        effectiveEndDate: validDate(self.editInterestRateItem.effectiveEnd),
        note: self.editInterestRateItem.note,
        activeStatus: self.editInterestRateItem.status === 'Active',
      };
      if (self.editInterestRateItem.productCategory === 'Deposit') {
        request.isNewUser = self.editInterestRateItem.isNewUser === true;
        request.categoryID = self.editInterestRateItem.categoryID || null;
        request.subCategoryID = self.editInterestRateItem.subCategoryID || null;
        request.accountName = self.editInterestRateItem.accountName || null;
      }
      self.$call.UpdateMetaDocInterestRate(request, function(res) {
        self.dialogEditInterestRate = false;
        self.loadInterestRates();
      });
    },
    loadInterestRates() {
      var self = this;
      if (!self.vectorDBName) return;
      self.$call.GetMetaDocInterestRates(
        { vectorDBName: self.vectorDBName },
        function(dataResponse) {
          if (dataResponse.status == 0 && dataResponse.data) {
            // Sort interest rates latest to oldest (descending by version string e.g. V003 > V002 > V001)
            var sorted = dataResponse.data.slice().sort(function(a, b) {
              var va = String(a.version || ''); var vb = String(b.version || '');
              return vb.localeCompare(va);
            });
            self.interestRateData = sorted.map(function(item) {
              return {
                id: item.id,
                version: item.version,
                accountCode: item.accountCode || '',
                accountName: item.accountName || '',
                productCategory: item.productCategory,
                productType: item.productType,
                productSubtype: item.productSubtype,
                categoryID: item.categoryID,
                subCategoryID: item.subCategoryID,
                calculationMethod: item.calculationMethod,
                paymentFrequency: item.paymentFrequency,
                tiers: item.tiers || [],
                variants: item.variants || [],
                mrrRate: item.mrrRate,
                interestRateName: item.interestRateName,
                interestRateType: item.interestRateType,
                loanCategoryID: item.loanCategoryID,
                isNewUser: item.isNewUser === true || item.IsNewUser === true,
                status: item.activeStatus ? 'Active' : 'Inactive',
                effectiveStartDate: self.formatDate(item.effectiveStartDate),
                effectiveEndDate: self.formatDate(item.effectiveEndDate),
                effectiveStartDateRaw: item.effectiveStartDate,
                effectiveEndDateRaw: item.effectiveEndDate,
                addedBy: item.createBy || '-',
                addedDate: self.formatUtcDate(item.createDate),
                updateBy: item.updateBy || '-',
                updatedDate: self.formatUtcDate(item.updateDate),
                note: item.note || '',
              };
            });
          } else {
            self.interestRateData = [];
          }
        }
      );
    },
    loadCategories() {
      var self = this;
      self.loadingCategories = true;
      self.$call.GetInterestRateCategories(
        function(response) {
          if (response && response.data) {
            self.categoriesData = response.data.interestRateCategories || [];
            self.occupationsData = response.data.occupations || [];
          }
          self.loadingCategories = false;
        },
        function(error) {
          console.error('Error loading categories:', error);
          self.loadingCategories = false;
        }
      );
    },
    goBack() {
      const titleSubTitleMap = {
        'Product': ['Deposit', 'Loan', 'Insurance', 'Card'],
        'ServiceInfo': ['Service_Info', 'Page_Navigation', 'Branch_Navigation'],
        'Financial_Transaction': ['Transfer', 'TopUp', 'Paybill'],
      };
      let parentTab = this.productCategory || undefined;
      let subTab = this.fromSubTab || undefined;
      for (const [tab, subtitles] of Object.entries(titleSubTitleMap)) {
        if (subtitles.includes(this.productCategory)) {
          parentTab = tab;
          if (!subTab) {
            subTab = this.productCategory;
          }
          break;
        }
      }
      this.$router.push({ name: 'MetaDoc', query: { tab: parentTab, subTab: subTab } });
    },
    processingChipColor(status) {
      if (status === 'Completed') return 'success';
      if (status === 'InProcess') return 'warning';
      if (status === 'Refining') return 'purple';
      if (status === 'Failed') return 'error';
      return 'grey';
    },
    processingChipIcon(status) {
      if (status === 'Completed') return 'mdi-check-circle';
      if (status === 'InProcess') return 'mdi-clock-outline';
      if (status === 'Refining') return 'mdi-auto-fix';
      if (status === 'Failed') return 'mdi-alert-circle-outline';
      return 'mdi-progress-clock';
    },
    normalizeStatusProcess(status) {
      if (status === 'Success') return 'Completed';
      if (status === 'Error') return 'Failed';
      return status;
    },
    // #720: LLM refine progress, e.g. "Refining 3/10"
    statusProcessLabel(v) {
      const status = this.normalizeStatusProcess(v.statusProcess);
      if (status === 'Refining' && v.inProcessMax) return `Refining ${v.inProcess || 0}/${v.inProcessMax}`;
      return status;
    },
    isViewDisabled(item) {
      const s = item && item.statusProcess;
      return s === 'Pending' || s === 'InProcess' || s === 'Refining';
    },
    loadDocumentVersions() {
      let self = this;
      if (!self.vectorDBName) return;

      self.loadingTable = true;
      self.$call.GetDocumentVersions(
        { vectorDBName: self.vectorDBName },
        (dataResponse) => {
          self.loadingTable = false;
          if (dataResponse.status == 0 && dataResponse.data) {
            const versions = dataResponse.data.versions || [];
            // Sort versions latest to oldest (descending by version number)
            versions.sort((a, b) => (b.version || 0) - (a.version || 0));
            self.tableData = versions.map((v) => ({
              id: v.id,
              version: v.versionLabel || `V${(v.version || 1).toString().padStart(3, '0')}`,
              status: v.activeStatus ? 'Active' : 'Inactive',
              activeStatus: v.activeStatus,
              fileName: v.fileName || '-',
              effectiveStartDate: self.formatDate(v.effectiveStartDate),
              effectiveEndDate: self.formatDate(v.effectiveEndDate),
              effectiveStartDateRaw: v.effectiveStartDate,
              effectiveEndDateRaw: v.effectiveEndDate,
              universalLink: v.universalLink || '-',
              webLink: v.webLink || '-',
              json: v.jsonData || '-',
              channelAvailability: v.channelAvailability ? (typeof v.channelAvailability === 'string' ? JSON.parse(v.channelAvailability).join(', ') : v.channelAvailability) : '-',
              addedBy: v.createBy || '-',
              addedDate: self.formatUtcDate(v.createDate),
              updateBy: v.updateBy || '-',
              updatedDate: self.formatUtcDate(v.updateDate),
              note: v.note || '-',
              statusProcess: self.normalizeStatusProcess(v.statusProcess) || null,
              statusProcessLabel: self.statusProcessLabel(v) || null,
            }));
            self.totalItems = versions.length;
          } else {
            self.tableData = [];
            self.totalItems = 0;
          }
        },
        (errorResource) => {
          self.loadingTable = false;
          console.error('GetDocumentVersions error:', errorResource);
        }
      );
    },
  },
  mounted() {
    this.GetDate();
    this.vectorDBName = this.$route.query.name || '';
    this.productCategory = this.$route.query.productCategory || '';
    this.subTitle = this.$route.query.subTitle || '';
    this.productSubtype = this.$route.query.productSubtype || '';
    this.itemID = this.$route.query.itemID || '';
    this.parentDocNameEN = this.$route.query.nameEN || '';
    this.parentDocNameTH = this.$route.query.nameTH || '';
    this.fromSubTab = this.$route.query.fromSubTab || '';
    this.loadDocumentVersions();
    if (this.showInterestRateControl) {
      this.loadInterestRates();
      this.loadCategories();
    }
    window.addEventListener('resize', this.onResizeWindow);
    // Auto-refresh every 10 seconds disabled.
    // this.refreshIntervalId = setInterval(() => {
    //   if (this.tableData.length > 0 && !this.loadingTable) {
    //     this.loadDocumentVersions();
    //   }
    // }, 10000);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResizeWindow);
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.v-toolbar) {
  border-radius: 12px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

/* Document Table — Excel-like scroll */
.doc-table-container {
  max-width: 100%;
  position: relative;
}

:deep(.doc-table) {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.doc-table table) {
  min-width: 1880px;
  table-layout: fixed;
}

:deep(.doc-table .v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  position: sticky;
  top: 0;
  z-index: 5;
}

/* Sticky Actions column (col 1, left: 0, width: 180px) */
.doc-table :deep(.v-table__wrapper table thead tr th:first-child),
.doc-table :deep(.v-table__wrapper table tbody tr td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 4 !important;
  width: 180px !important;
  min-width: 180px !important;
  max-width: 180px !important;
  background-color: #f5f5f5 !important;
}

.doc-table :deep(.v-table__wrapper table tbody tr td:first-child) {
  background-color: #fff !important;
  z-index: 4 !important;
}

/* Sticky Version column (col 2, left: 180px, width: 100px) */
.doc-table :deep(.v-table__wrapper table thead tr th:nth-child(2)),
.doc-table :deep(.v-table__wrapper table tbody tr td:nth-child(2)) {
  position: sticky !important;
  left: 180px !important;
  z-index: 4 !important;
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
  background-color: #f5f5f5 !important;
}

.doc-table :deep(.v-table__wrapper table tbody tr td:nth-child(2)) {
  background-color: #fff !important;
  z-index: 4 !important;
}

/* Sticky Status column (col 3, left: 280px, width: 100px) */
.doc-table :deep(.v-table__wrapper table thead tr th:nth-child(3)),
.doc-table :deep(.v-table__wrapper table tbody tr td:nth-child(3)) {
  position: sticky !important;
  left: 280px !important;
  z-index: 4 !important;
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
  background-color: #f5f5f5 !important;
}

.doc-table :deep(.v-table__wrapper table tbody tr td:nth-child(3)) {
  background-color: #fff !important;
  z-index: 4 !important;
}


:deep(.v-data-table tbody tr) {
  transition: background-color 0.2s ease;
}

.doc-table :deep(.v-table__wrapper),
.doc-table :deep(.v-data-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
  max-height: 624px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
  border-radius: 1rem 1rem 0 0;
  background: #f5f5f5 !important;
}

.doc-table :deep(.v-table__wrapper table),
.doc-table :deep(.v-data-table__wrapper table),
.doc-table :deep(table) {
  width: max-content !important;
  table-layout: fixed !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.doc-table :deep(thead tr th),
.doc-table :deep(tbody tr td) {
  vertical-align: top !important;
  box-sizing: border-box !important;
  padding: 8px 6px !important;
}

.doc-table :deep(thead tr th) {
  background: #f5f5f5 !important;
  white-space: pre-line !important;
  word-break: normal !important;
  overflow-wrap: break-word !important;
  line-height: 1.2rem !important;
}

.doc-table :deep(thead tr th:first-child) {
  border-top-left-radius: 1rem;
}

.doc-table :deep(thead tr th:last-child) {
  border-top-right-radius: 1rem;
}

.doc-table :deep(.v-data-table-header__content) {
  white-space: pre-line !important;
  line-height: 1.2rem !important;
}

.doc-table :deep(thead tr th:nth-child(4)),
.doc-table :deep(thead tr th:nth-child(4) .v-data-table-header__content),
.doc-table :deep(thead tr th:nth-child(8)),
.doc-table :deep(thead tr th:nth-child(8) .v-data-table-header__content),
.doc-table :deep(thead tr th:nth-child(11)),
.doc-table :deep(thead tr th:nth-child(11) .v-data-table-header__content) {
  white-space: nowrap !important;
}

.doc-table :deep(tbody tr td) {
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

/* White body rows — override Vuetify's default #fafafa / alternating gray */
.doc-table :deep(tbody tr),
.doc-table :deep(tbody tr.doc-table-row) {
  background-color: #ffffff !important;
}

/* Remove any striped / alternate row coloring */
.doc-table :deep(tbody tr:nth-child(even)),
.doc-table :deep(tbody tr:nth-child(odd)) {
  background-color: #ffffff !important;
}

/* Subtle hover — keep it white-ish, not gray */
.doc-table :deep(tbody tr:hover),
.doc-table :deep(tbody tr.doc-table-row:hover) {
  background-color: #f8f9fb !important;
}

/* Column widths */
.doc-table :deep(thead tr th:nth-child(1)),
.doc-table :deep(tbody tr td:nth-child(1)) {
  width: var(--doc-col-1) !important; min-width: var(--doc-col-1) !important; max-width: var(--doc-col-1) !important;
}
.doc-table :deep(thead tr th:nth-child(2)),
.doc-table :deep(tbody tr td:nth-child(2)) {
  width: var(--doc-col-2) !important; min-width: var(--doc-col-2) !important; max-width: var(--doc-col-2) !important;
}
.doc-table :deep(thead tr th:nth-child(3)),
.doc-table :deep(tbody tr td:nth-child(3)) {
  width: var(--doc-col-3) !important; min-width: var(--doc-col-3) !important; max-width: var(--doc-col-3) !important;
}
.doc-table :deep(thead tr th:nth-child(4)),
.doc-table :deep(tbody tr td:nth-child(4)) {
  width: var(--doc-col-4) !important; min-width: var(--doc-col-4) !important; max-width: var(--doc-col-4) !important;
}
.doc-table :deep(thead tr th:nth-child(5)),
.doc-table :deep(tbody tr td:nth-child(5)) {
  width: var(--doc-col-5) !important; min-width: var(--doc-col-5) !important; max-width: var(--doc-col-5) !important;
}
.doc-table :deep(thead tr th:nth-child(6)),
.doc-table :deep(tbody tr td:nth-child(6)) {
  width: var(--doc-col-6) !important; min-width: var(--doc-col-6) !important; max-width: var(--doc-col-6) !important;
}
.doc-table :deep(thead tr th:nth-child(7)),
.doc-table :deep(tbody tr td:nth-child(7)) {
  width: var(--doc-col-7) !important; min-width: var(--doc-col-7) !important; max-width: var(--doc-col-7) !important;
}
.doc-table :deep(thead tr th:nth-child(8)),
.doc-table :deep(tbody tr td:nth-child(8)) {
  width: var(--doc-col-8) !important; min-width: var(--doc-col-8) !important; max-width: var(--doc-col-8) !important;
}
.doc-table :deep(thead tr th:nth-child(9)),
.doc-table :deep(tbody tr td:nth-child(9)) {
  width: var(--doc-col-9) !important; min-width: var(--doc-col-9) !important; max-width: var(--doc-col-9) !important;
}
.doc-table :deep(thead tr th:nth-child(10)),
.doc-table :deep(tbody tr td:nth-child(10)) {
  width: var(--doc-col-10) !important; min-width: var(--doc-col-10) !important; max-width: var(--doc-col-10) !important;
}
.doc-table :deep(thead tr th:nth-child(11)),
.doc-table :deep(tbody tr td:nth-child(11)) {
  width: var(--doc-col-11) !important; min-width: var(--doc-col-11) !important; max-width: var(--doc-col-11) !important;
}
.doc-table :deep(thead tr th:nth-child(12)),
.doc-table :deep(tbody tr td:nth-child(12)) {
  width: var(--doc-col-12) !important; min-width: var(--doc-col-12) !important; max-width: var(--doc-col-12) !important;
}
.doc-table :deep(thead tr th:nth-child(13)),
.doc-table :deep(tbody tr td:nth-child(13)) {
  width: var(--doc-col-13) !important; min-width: var(--doc-col-13) !important; max-width: var(--doc-col-13) !important;
}

.doc-table :deep(thead tr th:nth-child(1)),
.doc-table :deep(thead tr th:nth-child(2)),
.doc-table :deep(thead tr th:nth-child(3)),
.doc-table :deep(tbody tr td:nth-child(1)),
.doc-table :deep(tbody tr td:nth-child(2)),
.doc-table :deep(tbody tr td:nth-child(3)) {
  white-space: nowrap !important;
  overflow-wrap: normal !important;
  word-break: normal !important;
}

/* Outer wrapper — horizontal scroll, no clip */
.doc-table-outer {
  border-radius: 1rem 1rem 0 0;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.1);
}

/* =====================================================
   DOC CELL helpers
   ===================================================== */
.doc-cell {
  width: 100%;
}

.doc-cell--wrap {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.doc-cell--link {
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.doc-cell--clamp-3 {
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.doc-cell--expanded {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: block;
  overflow: visible;
  -webkit-line-clamp: unset;
}

.doc-table :deep(.v-table__wrapper table tbody tr:hover td:first-child),
.doc-table :deep(.v-table__wrapper table tbody tr:hover td:nth-child(2)),
.doc-table :deep(.v-table__wrapper table tbody tr:hover td:nth-child(3)) {
  background-color: #f8f9fb !important;
}

/* Custom scrollbar — Document table */
:deep(.doc-table .v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

:deep(.doc-table .v-table__wrapper)::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

:deep(.doc-table .v-table__wrapper)::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}

:deep(.doc-table .v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.35);
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.7) !important;
  font-size: 0.875rem !important;
}

/* Interest Rate Table — Excel-like scroll */
.interest-table-container {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

:deep(.interest-table) {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.interest-table .v-table__wrapper) {
  max-height: 624px;
  overflow: auto !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
}

:deep(.interest-table table) {
  min-width: 1130px;
  table-layout: fixed;
}

:deep(.interest-table .v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Sticky Actions column (col 1, left: 0, width: 130px) */
:deep(.interest-table .v-table__wrapper table thead tr th:first-child),
:deep(.interest-table .v-table__wrapper table tbody tr td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 1 !important;
  width: 130px !important;
  min-width: 130px !important;
  max-width: 130px !important;
}

:deep(.interest-table .v-table__wrapper table thead tr th:first-child) {
  top: 0 !important;
  background-color: #f5f5f5 !important;
  z-index: 3 !important;
}

:deep(.interest-table .v-table__wrapper table tbody tr td:first-child) {
  background-color: #fff !important;
}

/* Sticky Version column (col 2, left: 130px, width: 100px) */
:deep(.interest-table .v-table__wrapper table thead tr th:nth-child(2)),
:deep(.interest-table .v-table__wrapper table tbody tr td:nth-child(2)) {
  position: sticky !important;
  left: 130px !important;
  z-index: 1 !important;
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
}

:deep(.interest-table .v-table__wrapper table thead tr th:nth-child(2)) {
  top: 0 !important;
  background-color: #f5f5f5 !important;
  z-index: 3 !important;
}

:deep(.interest-table .v-table__wrapper table tbody tr td:nth-child(2)) {
  background-color: #fff !important;
}

/* Sticky Status column (col 3, left: 230px, width: 100px) */
:deep(.interest-table .v-table__wrapper table thead tr th:nth-child(3)),
:deep(.interest-table .v-table__wrapper table tbody tr td:nth-child(3)) {
  position: sticky !important;
  left: 230px !important;
  z-index: 1 !important;
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
}

:deep(.interest-table .v-table__wrapper table thead tr th:nth-child(3)) {
  top: 0 !important;
  background-color: #f5f5f5 !important;
  z-index: 3 !important;
}

:deep(.interest-table .v-table__wrapper table tbody tr td:nth-child(3)) {
  background-color: #fff !important;
}


:deep(.interest-table .v-table__wrapper table tbody tr:hover td:first-child),
:deep(.interest-table .v-table__wrapper table tbody tr:hover td:nth-child(2)),
:deep(.interest-table .v-table__wrapper table tbody tr:hover td:nth-child(3)) {
  background-color: #fafafa !important;
}

/* Custom scrollbar — Interest Rate table */
:deep(.interest-table .v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

:deep(.interest-table .v-table__wrapper)::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

:deep(.interest-table .v-table__wrapper)::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}

:deep(.interest-table .v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.35);
}

.title-tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.title-tabs :deep(.v-tab) {
  text-transform: none !important;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0;
}

.title-tabs :deep(.v-tab--selected) {
  color: #4B2869 !important;
  font-weight: 600;
}

.custom-document-btn {
  color: #ffff;
  text-transform: none;
  box-shadow: 0 2px 4px rgba(80, 72, 151, 0.3);
  border-radius: 16px !important;
  height: 48px !important;
  min-width: 170px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
}

.custom-document-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(80, 72, 151, 0.4);
}

.action-buttons-group {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  min-width: 36px !important;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.action-btn .v-icon {
  font-size: 14px;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.expand-btn {
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  flex-shrink: 0;
}

.status-chip {
  font-weight: 500 !important;
  border-radius: 8px !important;
  min-width: 80px;
  justify-content: center;
}

.toggle-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  background: rgba(25, 118, 210, 0.05) !important;
}

/* Add New Document Dialog */
.add-doc-dialog-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.add-doc-dialog-header {
  background: linear-gradient(90deg, #f8a4c8, #f8c0d8);
  padding: 16px 24px;
}

.add-doc-dialog-header h2 {
  font-weight: 700;
  font-size: 20px;
  color: #000;
  margin: 0;
}

.field-label {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  display: block;
  margin-bottom: 4px;
}

/* #720 LLM Content Refinement toggle bar */
.refine-toggle-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
  transition: background-color 0.2s, border-color 0.2s;
}
.refine-toggle-bar--on {
  border-color: #76237e;
  background: #f6eef7;
}
.refine-toggle-bar--disabled {
  opacity: 0.6;
}
.refine-toggle-bar__text {
  flex: 1;
  min-width: 0;
}
.refine-toggle-bar__title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}
.refine-toggle-bar__hint {
  font-size: 12px;
  color: #757575;
  line-height: 1.4;
}
.refine-toggle-bar__switch {
  flex: 0 0 auto;
}

.customer-scope-chip {
  font-weight: 600;
  letter-spacing: 0;
}

.customer-scope-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 12px 16px;
  border: 1px solid #d8dbe3;
  border-radius: 12px;
  background: #f8f9fb;
}

.customer-scope-title {
  color: #1f1f1f;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.customer-scope-description {
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 2px;
}

.upload-btn {
  text-transform: none;
  border-radius: 8px !important;
  height: 40px !important;
  min-width: 110px !important;
}

.custom-checkbox {
  font-size: 14px;
}

/* View Document Dialog */
.view-doc-dialog-card {
  border-radius: 12px !important;
  border: 1px solid #ccc;
}

.chunk-table-wrapper {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #333;
  margin-bottom: 4px;
}

.chunk-table {
  width: 100%;
  border-collapse: collapse;
}

.chunk-table th,
.chunk-table td {
  border: 1px solid #333;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
  font-size: 13px;
}

.chunk-table th {
  background: #f5f5f5;
  font-weight: 600;
  font-size: 13px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.chunk-cell {
  min-height: 80px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.copy-icon {
  opacity: 0.5;
  flex-shrink: 0;
}

.copy-icon:hover {
  opacity: 1;
}

/* Interest Rate Tier Table */
.interest-table :deep(.v-table__wrapper),
.interest-table :deep(.v-data-table__wrapper) {
  overflow-x: auto !important;
}

.interest-table :deep(thead tr th) {
  background: #f5f5f5 !important;
  font-weight: 600 !important;
  color: #000000 !important;
  white-space: nowrap !important;
}

.interest-table :deep(tbody tr) {
  transition: background-color 0.2s ease;
}

.interest-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

.tier-table-wrapper {
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  overflow-x: auto;
}

.tier-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
}

.tier-table th,
.tier-table td {
  border: 1px solid #e0e0e0;
  padding: 6px 8px;
  font-size: 13px;
  vertical-align: middle;
}

.tier-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.tier-table td .v-text-field {
  min-width: 80px;
}

/* ── Variant Panel Styles ── */
.variant-panels {
  gap: 16px;
}

.variant-panel {
  border: none !important;
  border-radius: 16px !important;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.variant-panel:hover {
  box-shadow: 0 8px 24px rgba(79, 57, 130, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
}

.variant-panel-title {
  background: linear-gradient(135deg, #fafbfd 0%, #f0f2f8 100%) !important;
  padding: 16px 20px !important;
  min-height: 72px !important;
  border-bottom: 1px solid rgba(79, 57, 130, 0.08);
}

.variant-panel-title:hover {
  background: linear-gradient(135deg, #f5f7fb 0%, #ebeef6 100%) !important;
}

.variant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.variant-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.variant-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.variant-number {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #504897 0%, #6b5bb3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(79, 57, 130, 0.3);
  flex-shrink: 0;
}

.variant-number-text {
  color: white;
  font-weight: 700;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.variant-input-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 8px 16px;
  border: 1px solid rgba(79, 57, 130, 0.15);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  max-width: 400px;
  flex: 1;
  transition: all 0.2s ease;
}

.variant-input-wrapper:hover {
  border-color: rgba(79, 57, 130, 0.3);
  box-shadow: 0 2px 8px rgba(79, 57, 130, 0.1);
}

.variant-input-wrapper:focus-within {
  border-color: #504897;
  box-shadow: 0 0 0 3px rgba(79, 57, 130, 0.15);
}

.variant-input-icon {
  color: #504897;
  opacity: 0.7;
  margin-right: 8px;
}

.variant-name-input {
  flex: 1;
}

.variant-name-input :deep(.v-field__input) {
  padding: 0 !important;
  min-height: 32px !important;
  font-weight: 500;
  color: #333;
  font-size: 15px;
}

.variant-name-input :deep(.v-field) {
  padding: 0 !important;
}

.default-calc-label {
  font-size: 12px;
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  margin-right: 4px;
}

.installment-type-select {
  min-width: 200px;
}

.installment-type-select :deep(.v-field__input) {
  padding: 0 8px !important;
  min-height: 32px !important;
  font-weight: 600;
  color: #1976d2;
  font-size: 13px;
}

.installment-type-select :deep(.v-field) {
  padding: 0 !important;
}

.variant-status-chip {
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 0 12px;
  height: 28px;
}

.variant-delete-btn {
  border-radius: 10px !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  padding: 0 12px !important;
  height: 36px !important;
}

.variant-delete-btn:hover {
  background: rgba(244, 67, 54, 0.15) !important;
}

.variant-panel :deep(.v-expansion-panel-title__overlay) {
  background: transparent !important;
}

.variant-panel :deep(.v-expansion-panel-title__icon) {
  color: #504897;
  margin-left: 8px;
}

/* ── Personal Loan Table Styling ── */
.personal-loan-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  position: relative;
}

.personal-loan-table-wrapper {
  display: inline-block;
  min-width: 100%;
}

.personal-loan-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  table-layout: auto;
}

.personal-loan-table thead tr {
  background-color: #fafafa;
}

.personal-loan-table th,
.personal-loan-table td {
  border: 1px solid #e0e0e0;
  padding: 12px 16px;
  white-space: nowrap;
  vertical-align: middle;
}

.personal-loan-table th {
  font-weight: 600;
  color: #666;
  position: sticky;
  top: 0;
  background-color: #fafafa;
  z-index: 10;
}

.personal-loan-table tbody tr:hover {
  background-color: #f5f5f5;
}

.personal-loan-table :deep(.v-text-field .v-field) {
  font-size: 14px;
}

.personal-loan-table :deep(.v-combobox .v-field) {
  font-size: 14px;
}

.personal-loan-scroll-container::-webkit-scrollbar {
  height: 10px;
}

.personal-loan-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 5px;
  margin: 0 10px;
}

.personal-loan-scroll-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

.personal-loan-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.personal-loan-table :deep(input[type="number"]::-webkit-outer-spin-button),
.personal-loan-table :deep(input[type="number"]::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.personal-loan-table :deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}

.custom-datetime-picker {
  width: 100%;
}

.custom-datetime-picker :deep(.dp__input_wrap) {
  position: relative;
}

.custom-datetime-picker :deep(.dp__input) {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  padding: 10px 40px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  background: white;
  height: 40px;
  width: 100%;
  box-sizing: border-box;
}

.custom-datetime-picker :deep(.dp__input:hover) {
  border-color: rgba(0, 0, 0, 0.87);
}

.custom-datetime-picker :deep(.dp__input_focus) {
  border-color: #1976d2;
  border-width: 2px;
}

.custom-datetime-picker :deep(.dp__input_icon) {
  left: 10px;
  right: auto;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.54);
  pointer-events: none;
}

.custom-datetime-picker :deep(.dp--clear-btn) {
  right: 10px;
}

:global(.non-loop-time-picker) {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
}

:global(.non-loop-time-column) {
  display: flex;
  flex-direction: column;
  width: 54px;
  height: 250px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  scroll-snap-type: y proximity;
}

:global(.non-loop-time-column::-webkit-scrollbar) {
  width: 6px;
}

:global(.non-loop-time-column::-webkit-scrollbar-thumb) {
  background: #c7c7c7;
  border-radius: 8px;
}

:global(.non-loop-time-option) {
  min-height: 32px;
  border: 0;
  background: transparent;
  color: #333;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  scroll-snap-align: center;
}

:global(.non-loop-time-option:hover) {
  background: #f5f5f5;
}

:global(.non-loop-time-option-active) {
  background: #1976d2;
  color: #fff;
}

:global(.non-loop-time-separator) {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  /* offset down by the header height (font 12px + gap 4px) so ":" lines up with the number columns */
  margin-top: 20px;
}

:global(.non-loop-time-group) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

:global(.non-loop-time-header) {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  letter-spacing: 0.5px;
}

:global(.dp-ok-action-row) {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: -10px;
}

:global(.dp-ok-btn) {
  background: #1976d2;
  color: #fff;
  border: 0;
  border-radius: 6px;
  padding: 6px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

:global(.dp-ok-btn:hover) {
  background: #1565c0;
}

:global(.side-by-side-datetime-menu) {
  width: auto !important;
  max-width: calc(100vw - 24px);
}

:global(.side-by-side-datetime-menu .dp__instance_calendar[role="document"]) {
  display: flex;
  align-items: stretch;
}

:global(.side-by-side-datetime-menu .dp__instance_calendar[role="document"] > .dp__menu_inner) {
  border-right: 1px solid #e0e0e0;
}

:global(.side-by-side-datetime-menu .non-loop-time-picker) {
  height: 100%;
  min-height: 306px;
  align-items: center;
}

@media (max-width: 600px) {
  :global(.side-by-side-datetime-menu) {
    width: var(--dp-menu-width) !important;
  }

  :global(.side-by-side-datetime-menu .dp__instance_calendar[role="document"]) {
    flex-direction: column;
  }

  :global(.side-by-side-datetime-menu .dp__instance_calendar[role="document"] > .dp__menu_inner) {
    border-right: 0;
    border-bottom: 1px solid #e0e0e0;
  }

  :global(.side-by-side-datetime-menu .non-loop-time-picker) {
    min-height: auto;
  }
}
</style>
